import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Sparkles, Loader2, RefreshCw, ChevronDown, ChevronLeft, ChevronRight, Lock, Palette } from 'lucide-react';

// Re-export data constants needed by both Dashboard and this modal
export const SUIT_COLORS = [
  { value: '#1C2841', name: 'Navy' }, { value: '#36454F', name: 'Charcoal' },
  { value: '#0B0B0B', name: 'Black' }, { value: '#808080', name: 'Grey' },
  { value: '#3B2F2F', name: 'Brown' }, { value: '#722F37', name: 'Burgundy' },
];
export const SHIRT_COLORS = [
  { value: '#FFFFFF', name: 'White' }, { value: '#ADD8E6', name: 'Light Blue' },
  { value: '#F5F5DC', name: 'Cream' }, { value: '#FFB6C1', name: 'Light Pink' },
  { value: '#D3D3D3', name: 'Light Grey' }, { value: '#0B0B0B', name: 'Black' },
];
export const PANT_COLORS = [
  { value: '#1C2841', name: 'Navy' }, { value: '#36454F', name: 'Charcoal' },
  { value: '#0B0B0B', name: 'Black' }, { value: '#808080', name: 'Grey' },
  { value: '#D2B48C', name: 'Tan' }, { value: '#F5F5DC', name: 'Cream' },
  { value: '#556B2F', name: 'Olive' }, { value: '#C19A6B', name: 'Camel' },
  { value: '#1A2E4A', name: 'Dark Denim' }, { value: '#3D5A80', name: 'Medium Jeans' },
  { value: '#7B90A8', name: 'Light Jeans' }, { value: '#1A1A1E', name: 'Black Denim' },
  // Feb 2026 — expanded palette per user request. Adds warm neutrals,
  // heritage tones, and cool blues that are common in dress trousers /
  // chinos but weren't previously selectable. Every hex is anchored to
  // real menswear cloth swatches so the render prompt reads cleanly.
  { value: '#B0A899', name: 'Stone' }, { value: '#9E8A5F', name: 'Khaki' },
  { value: '#D6C8A6', name: 'Sand' }, { value: '#F0E4C8', name: 'Ivory' },
  { value: '#6D2E30', name: 'Burgundy' }, { value: '#2E4A36', name: 'Forest Green' },
  { value: '#3F5B7A', name: 'Slate Blue' }, { value: '#A55932', name: 'Rust' },
  { value: '#5B3A22', name: 'Chocolate' }, { value: '#6E645C', name: 'Taupe' },
];
// ── Trouser cut vocabulary ─────────────────────────────────────────
// Feb 2026 — new pant cut vocabulary surfaced in the Custom-Fabric
// questionnaire (business-casual only) and the Restructure edit panel.
// The `tuxedo` flag marks the satin side-stripe option that only
// makes sense on a tuxedo silhouette; the frontend gates that pill
// so it only appears inside the tuxedo-only Silhouette Restructure
// section, never in business-casual controls.
export const TROUSER_CUTS = [
  { value: 'flat_front',   label: 'Flat front',   hint: 'No pleats · clean' },
  { value: 'side_tabs',    label: 'Side tabs',    hint: 'Adjusters, no belt loops' },
  { value: 'single_pleat', label: 'Single pleat', hint: 'One pleat per side' },
  { value: 'double_pleat', label: 'Double pleat', hint: 'Two pleats per side' },
  { value: 'cuffed',       label: 'Cuffs',        hint: 'Turned-up hem' },
];
export const TUXEDO_TROUSER_DETAIL = {
  value: 'satin_side_stripe',
  label: 'Satin side stripe',
  hint: 'Black galon down outer seam',
};
// ── Button colour palette ──────────────────────────────────────────
// Each entry carries a `base` (the button's primary tone) and a `hi`
// (the highlight tone used for the radial-gradient sheen that gives the
// chip its domed 3D-button appearance). `texture` drives the overlay
// pattern (horn = grain striations, metal = brushed sheen, pearl =
// iridescent shimmer, enamel = smooth gloss). `desc` is the short
// material description shown under the row on hover / selection.
export const BUTTON_COLORS = [
  { name: 'Black Horn',       base: '#1A1A1A', hi: '#3A3A3A', texture: 'horn',   desc: 'Natural buffalo horn · deep matte black with subtle striations' },
  { name: 'Dark Horn',        base: '#3A2818', hi: '#5A3828', texture: 'horn',   desc: 'Natural horn · rich espresso brown with organic grain' },
  { name: 'Light Horn',       base: '#6B4A2E', hi: '#9A6E45', texture: 'horn',   desc: 'Natural horn · warm caramel with soft cream highlights' },
  { name: 'Tortoise',         base: '#5A2F12', hi: '#A06030', texture: 'horn',   desc: 'Faux tortoiseshell · amber and bronze mottle on chestnut base' },
  { name: 'Mother of Pearl',  base: '#EFE9DA', hi: '#FFFFFF', texture: 'pearl',  desc: 'Iridescent pearl · cool whites with pink and blue shimmer' },
  { name: 'White',            base: '#F5F5F5', hi: '#FFFFFF', texture: 'enamel', desc: 'White corozo · clean matte white, classic dress finish' },
  { name: 'Brass',            base: '#A88838', hi: '#E2C898', texture: 'metal',  desc: 'Solid brass · warm yellow-gold tone, brushed finish' },
  { name: 'Gold',             base: '#B8985A', hi: '#F0D896', texture: 'metal',  desc: 'Polished gold · rich champagne with high mirror shine' },
  { name: 'Silver',           base: '#A8A8A8', hi: '#E0E0E0', texture: 'metal',  desc: 'Sterling silver · cool brushed nickel tone' },
  { name: 'Gunmetal',         base: '#2D3338', hi: '#5C656C', texture: 'metal',  desc: 'Gunmetal grey · charcoal metal with subtle blue undertone' },
  { name: 'Navy Enamel',      base: '#1C2841', hi: '#3F506E', texture: 'enamel', desc: 'Lacquered navy enamel · glossy deep blue, blazer-classic' },
  { name: 'Burgundy Enamel',  base: '#5A1820', hi: '#9A3F4E', texture: 'enamel', desc: 'Lacquered burgundy enamel · rich oxblood with high gloss' },
];
export const SHOE_OPTIONS = [
  { id: 'oxford', name: 'Oxford' }, { id: 'derby', name: 'Derby' },
  { id: 'loafer', name: 'Loafers' }, { id: 'monk_strap', name: 'Monk Strap' },
  { id: 'chelsea_boot', name: 'Chelsea Boots' },
  // Feb 2026 — casual court sneaker option per user spec. Rendered by
  // backend as a clean minimal white-leather low-top (Common Projects
  // silhouette). Colour override is suppressed for this style so the
  // Gemini prompt keeps it white.
  { id: 'sneaker', name: 'Sneakers' },
];
export const SHOE_COLORS = [
  { value: '#0B0B0B', name: 'Black' }, { value: '#8B4513', name: 'Brown' },
  { value: '#A0522D', name: 'Cognac' }, { value: '#722F37', name: 'Burgundy' },
  { value: '#D2B48C', name: 'Tan' },
];
export const SUIT_PATTERNS = [
  { value: 'solid', label: 'Solid' }, { value: 'pinstripe', label: 'Pinstripe' },
  { value: 'herringbone', label: 'Herringbone' }, { value: 'houndstooth', label: 'Houndstooth' },
  { value: 'windowpane', label: 'Windowpane' }, { value: 'glen_plaid', label: 'Glen Plaid' },
];
export const SHIRT_PATTERNS = [
  { value: 'solid', label: 'Solid' }, { value: 'stripes', label: 'Stripes' },
  { value: 'gingham', label: 'Gingham' }, { value: 'checks', label: 'Checks' },
];
export const LAPEL_OPTIONS = [{ value: 'notch', label: 'Notch' }, { value: 'peak', label: 'Peak' }, { value: 'shawl', label: 'Shawl' }];
export const BUTTON_OPTIONS = [
  { value: '1', label: '1-Button' },
  { value: '2', label: '2-Button' },
  { value: '3', label: '3-Button' },
  { value: 'double_breasted', label: 'Double-Breasted' },
];
export const BUTTON_STYLE_OPTIONS = [
  { value: 'matching', label: 'Matching' }, { value: 'horn', label: 'Horn' },
  { value: 'gold', label: 'Gold/Brass' }, { value: 'mother_of_pearl', label: 'Mother of Pearl' },
  { value: 'contrast', label: 'Contrast' },
];
export const TROUSER_STYLES = [
  { value: 'flat_front', label: 'Flat Front' }, { value: 'high_waisted', label: 'High-Waisted' },
  { value: 'pleated', label: 'Pleated' }, { value: 'cuffed', label: 'Cuffed' },
  { value: 'tapered', label: 'Tapered' },
];
export const TIE_OPTIONS = [
  { value: 'none', label: 'No Tie' }, { value: 'silk_solid', label: 'Silk Solid' },
  { value: 'striped', label: 'Striped' }, { value: 'polka_dot', label: 'Polka Dot' },
  { value: 'paisley', label: 'Paisley' }, { value: 'knit', label: 'Knit' },
  { value: 'bow_tie', label: 'Bow Tie' },
];
export const TIE_COLORS = [
  { value: '#1C2841', name: 'Navy' }, { value: '#722F37', name: 'Burgundy' },
  { value: '#0B0B0B', name: 'Black' }, { value: '#808080', name: 'Grey' },
  { value: '#556B2F', name: 'Forest Green' }, { value: '#B8860B', name: 'Gold' },
  { value: '#4A4A7A', name: 'Slate Blue' }, { value: '#D2B48C', name: 'Tan' },
];

// Feb 2026 — dress-shirt collar / cuff options surfaced in the
// Customize modal. Casual shirts get only a pant edit per user spec;
// dress shirts get collar + cuff + pant edits.
export const SHIRT_COLLAR_OPTIONS = [
  { value: 'semi_spread', label: 'Semi-spread' },
  { value: 'spread',      label: 'Wide spread' },
  { value: 'point',       label: 'Point' },
  { value: 'cutaway',     label: 'Cutaway' },
  { value: 'button_down', label: 'Button-down' },
];
export const SHIRT_CUFF_OPTIONS = [
  { value: 'barrel', label: 'Barrel' },
  { value: 'french', label: 'French (double)' },
];

// ────────────────────────────────────────────────────────────────
// Bespoke garment FEATURES — canonical copy used in the Features
// accordion inside the Customize modal. Branched PER GARMENT TYPE
// so a suit shows suit-construction features, a sports coat shows
// soft-tailoring features, a safari jacket shows utility features,
// a shirt shows shirt features, etc. Real menswear research.
// ────────────────────────────────────────────────────────────────
const SUIT_FEATURES = [
  { key: 'light_roped_shoulder', title: 'Light roped shoulder',
    benefit: "A subtle peak where the sleevehead meets the shoulder — just enough rope to give the jacket presence and a strong silhouette in profile, without the rigid 80s-power-suit look. Communicates structure and intention; reads as authoritative on tall and short frames alike." },
  { key: 'broad_peak_lapels', title: 'Hand-padded peak lapels',
    benefit: "Wide peak lapels hand-padded by a master tailor with horse-hair canvas underneath, giving the lapel a deep, sculpted roll instead of lying flat. Visually broadens the chest, narrows the waist, and elongates the jacket line. Sits at exactly the right pitch to frame the tie knot and pull the eye up to the face." },
  { key: 'trim_sleeves', title: "Trim sleeves with surgeon's cuffs",
    benefit: "Cleanly tapered from bicep to cuff with minimal excess fabric, finished with four functional buttonholes (surgeon's cuffs) you can actually unfasten. The mark of a true bespoke garment. Half an inch of shirt cuff exposed at the wrist; nothing more, nothing less." },
  { key: 'three_layer_canvas', title: '3-layer chest canvas',
    benefit: "Three independent layers of horsehair-and-wool canvas float between the outer cloth and the lining across the chest. Over time the canvas moulds to the client's posture, giving the jacket a living sculpted shape that fused (glued) construction can never replicate. Fused jackets bubble and delaminate after 3–5 years; a 3-layer canvas garment stays crisp for 20+." },
  { key: 'lower_button_longer_torso', title: 'Lower button stance · longer torso',
    benefit: "The front button sits slightly lower than the natural waistline and the jacket runs a touch longer than the standard ready-to-wear cut. Lengthens the upper body visually, balances the trouser proportion, and makes the lapel's V-roll feel deep and architectural. Particularly flattering for clients with athletic builds or shorter torsos." },
];

const SPORTS_COAT_FEATURES = [
  { key: 'soft_neapolitan_shoulder', title: 'Soft Neapolitan shoulder',
    benefit: "A spalla camicia (shirt-style) shoulder construction with minimal padding and a hand-finished mappina (puckered) sleevehead. Lighter and more relaxed than the roped business-suit shoulder — reads as Italian sprezzatura without sacrificing structure through the chest." },
  { key: 'patch_pockets', title: 'Hand-stitched patch pockets',
    benefit: "Patch pockets sewn directly onto the jacket front instead of cut welt pockets. Lower-key, softer, and unmistakably odd-jacket — the signal that this is meant to be worn with chinos and an open collar, not a tie." },
  { key: 'open_quarters', title: 'Open front quarters',
    benefit: "The bottom front of the jacket cuts back more sharply than a city suit, exposing more of the trouser and creating an elegant, relaxed flair when the jacket is unbuttoned. A defining feature of the Italian sports coat tradition." },
  { key: 'half_canvas', title: 'Half-canvas construction',
    benefit: "Hand-padded canvas runs through the chest and lapels, with fused construction only in the lower skirt. Lighter than a full canvas (better for warm-weather wools and linens) while preserving the natural drape and longevity of the upper jacket — the right choice for a sports coat." },
];

const SAFARI_JACKET_FEATURES = [
  { key: 'bush_collar', title: 'Structured bush collar',
    benefit: "A pointed shirt-style collar with notched revers — the historic British safari signature. Distinct from a blazer's wide lapels or a band-collar coat; gives the jacket clear hunting-jacket provenance while still framing the face cleanly." },
  { key: 'epaulettes', title: 'Functional epaulettes',
    benefit: "Cloth shoulder straps secured with a horn button at the collarbone. Originally for resting a rifle sling; today they add architectural intent across the shoulder line and emphasise the military bone-structure of the garment." },
  { key: 'four_patch_pleat_pockets', title: 'Four pleated patch pockets',
    benefit: "Two chest, two cargo-style hip — all with inverted box pleats so they expand under load. Utility from the colonial campaign tradition; today they make the jacket sit forward off the body and balance the silhouette." },
  { key: 'self_fabric_belt', title: 'Self-fabric waist belt',
    benefit: "A built-in cloth belt that cinches the waist via a horn or brass buckle. Lets the wearer dial the suppression up or down for the day — defining feature of the Sahariana / safari tradition and the cleanest way to give a soft jacket real shape." },
  { key: 'half_canvas', title: 'Half-canvas construction',
    benefit: "Lightweight half-canvas through the chest with an unlined or half-lined back, optimised for cotton-twill, linen and tropical wool. Breathable, easy to pack, holds its shape across hot-weather wear." },
];

const SHIRT_FEATURES = [
  { key: 'spread_collar', title: 'Semi-spread collar',
    benefit: "A medium-spread collar (75–90°) cut from interlined cotton with a fused tipping — the most versatile collar in menswear. Frames a Four-in-Hand or Half-Windsor tie cleanly, and looks composed open at the throat with no tie. The default for any dress shirt that lives in both boardroom and dinner-table." },
  { key: 'split_yoke', title: 'Split yoke (shoulder match)',
    benefit: "The back yoke is cut in two pieces with the grain mirrored on each side, allowing the tailor to match stripe or pattern direction precisely across the shoulders. A signal of a properly made shirt; only possible at the made-to-measure level." },
  { key: 'mop_buttons', title: 'Mother-of-pearl buttons',
    benefit: "Hand-cut from Australian mother-of-pearl with cross-stitched attachment. Heavier than plastic, refracts light with a quiet shimmer, and ages without yellowing. Replaces every shell button on the front placket and cuffs." },
  { key: 'single_needle_taper', title: 'Single-needle taper · 22 SPI',
    benefit: "Side seams sewn one stitch at a time at 22 stitches per inch (vs 8–10 on factory shirts). The result is a flat, clean seam with no puckering — the shirt drapes against the torso like a custom garment rather than an off-rack box." },
  { key: 'choice_of_cuff', title: 'Barrel or French (double) cuff',
    benefit: "Two-button barrel for daily wear; French (double) cuff with silk-knot or metal links for formal occasions. Either way the cuff sits ½ inch past the jacket sleeve at the wrist." },
];

const OUTERWEAR_FEATURES = [
  { key: 'wide_lapels', title: 'Generous notch or peak lapels',
    benefit: "Outerwear lapels run wider than a suit's to balance the heavier cloth and longer silhouette. Cut with a deeper roll and hand-padded for a sculpted curve that frames the face from a distance." },
  { key: 'storm_throat_latch', title: 'Storm flap · throat latch',
    benefit: "An overlapping storm flap across the chest plus a hidden throat latch that buttons across the lapel — original British weather-protection details from the trench-coat tradition. Functional in real wind, and a quiet luxury cue when worn open." },
  { key: 'bemberg_silk_lining', title: 'Bemberg or silk full lining',
    benefit: "A full-bodied lining cut from Bemberg cupro (or a printed silk twill upgrade) that lets the coat slide effortlessly over a suit jacket without binding. The colour or print is a private styling choice — flashes only when the coat is hung open." },
  { key: 'functional_buttonholes', title: 'Functional buttonholes',
    benefit: "Every button down the front, on the sleeve and across the storm flap is sewn through and unfastenable. Communicates that the coat is built to be worn — not a decorative ready-to-wear shell." },
  { key: 'half_belt_back', title: 'Half-belt or back yoke',
    benefit: "Trench coats use a full belt; overcoats and car coats are finished with a fixed half-belt or a clean yoke seam across the back. Suppresses the waist and stops the coat from reading as a sack on a tall frame." },
];

// ────────────────────────────────────────────────────────────────────
// Lapel-aware variants for the `broad_peak_lapels` SUIT feature.
// User rule (Feb 2026): the Features list must NEVER claim "peak lapels"
// when the actual rendered look has a shawl lapel (tuxedo / smoking
// jacket / dinner jacket) or a notch lapel (most business suits, knit
// blazers). Detect the lapel from `look.lapel`, `look.lapel_override`,
// or heuristics on `garment_id` / fabric_note, then swap the right copy
// into the SUIT_FEATURES list before rendering.
// ────────────────────────────────────────────────────────────────────
const LAPEL_FEATURE_VARIANTS = {
  peak: {
    key: 'hand_padded_lapels', title: 'Hand-padded lapels',
    benefit: "Lapels hand-padded by a master tailor with horse-hair canvas underneath, giving the lapel a deep, sculpted roll instead of lying flat. Sits at exactly the right pitch to frame the tie knot and pull the eye up to the face.",
  },
  notch: {
    key: 'hand_padded_lapels', title: 'Hand-padded lapels',
    benefit: "Lapels hand-padded by a master tailor with horse-hair canvas underneath, giving the lapel a deep, sculpted roll instead of lying flat. Sits at exactly the right pitch to frame the tie knot and pull the eye up to the face.",
  },
  shawl: {
    key: 'hand_padded_shawl_lapels', title: 'Hand-padded silk shawl lapels',
    benefit: "Continuous shawl-collar lapels in silk satin or grosgrain, hand-padded onto wool canvas for a deep sculpted roll. The signature lapel of a proper dinner jacket / smoking jacket — frames the bow tie cleanly, elongates the silhouette under chandelier light, and signals classic black-tie provenance.",
  },
};

function _lapelKindFor(look) {
  if (!look) return 'peak';
  const explicit = String(look.lapel || look.lapel_override || '').toLowerCase().trim();
  if (explicit === 'peak' || explicit === 'notch' || explicit === 'shawl') return explicit;
  // Heuristic fallback — derive from garment id + free-text fabric note.
  const gid = String(look.garment_id || look.garment_type || '').toLowerCase();
  const note = String(look.fabric_note || '').toLowerCase();
  const haystack = `${gid} ${note}`;
  if (/\bshawl\b/.test(haystack) || gid === 'tuxedo' || /\b(smoking jacket|dinner jacket|black[\s-]?tie|tuxedo)\b/.test(note)) {
    return 'shawl';
  }
  if (/\bpeak\b/.test(haystack) || /double[\s-]breast/.test(haystack)) return 'peak';
  // Sport coats, blazers, knit blazers and ordinary business suits → notch.
  return 'notch';
}

// Resolver — branches on category first, then garment_id for ambiguous cases.
// `look.category` carries values like full_suits / business_casual / wedding /
// outerwear / shirts; `look.garment_id` carries the specific piece (single_blazer,
// safari_jacket, polo_shirt, peacoat, etc.).
export function featuresFor(look) {
  if (!look) return SUIT_FEATURES;
  const cat = (look.category || '').toLowerCase();
  const gid = (look.garment_id || look.garment_type || '').toLowerCase();
  if (gid.includes('safari')) return SAFARI_JACKET_FEATURES;
  if (cat.startsWith('shirts') || gid.includes('shirt') || gid.includes('polo')) return SHIRT_FEATURES;
  if (cat.startsWith('outerwear') || ['peacoat','overcoat','trench','car_coat','mandarin_coat'].some(k => gid.includes(k))) return OUTERWEAR_FEATURES;
  if (cat.startsWith('business_casual') || gid.includes('blazer') || gid.includes('sport')) {
    // Sports coats / blazers — notch is the default lapel; swap the
    // lapel-specific entry into the sports-coat feature list if applicable.
    const lk = _lapelKindFor(look);
    const variant = LAPEL_FEATURE_VARIANTS[lk] || LAPEL_FEATURE_VARIANTS.notch;
    // Sports-coat list doesn't carry a lapel entry, so APPEND only when the
    // garment has a noteworthy lapel (peak or shawl — notch is the default).
    if (lk === 'peak' || lk === 'shawl') {
      return [...SPORTS_COAT_FEATURES, variant];
    }
    return SPORTS_COAT_FEATURES;
  }
  // full_suits, wedding, custom_fabric, custom_combo → suit features w/ correct lapel.
  const lapelKind = _lapelKindFor(look);
  const lapelVariant = LAPEL_FEATURE_VARIANTS[lapelKind] || LAPEL_FEATURE_VARIANTS.peak;
  return SUIT_FEATURES.map(f => (f.key === 'broad_peak_lapels' ? lapelVariant : f));
}

// Legacy alias — older code paths still reference this name.
const FEATURE_LIBRARY = SUIT_FEATURES;

// Backward-compat alias — older code paths still reference UPGRADE_LIBRARY.
// Safe to remove once all internal callers migrate to FEATURE_LIBRARY.
const UPGRADE_LIBRARY = FEATURE_LIBRARY;

// All 5 canonical features always render for every look.
const ALWAYS_SHOW_KEYS = FEATURE_LIBRARY.map((f) => f.key);

function matchUpgrade() {
  // Features are fixed per house style — we no longer enrich from Gemini's
  // free-form upgrade strings. Kept as a no-op for compat.
  return null;
}

function UpgradeItem({ title, benefit, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const expandable = Boolean(benefit);
  return (
    <li className="border-b border-[#27272A]/60 last:border-b-0" data-testid={`upgrade-item-${title.toLowerCase().replace(/\s+/g, '-').slice(0, 40)}`}>
      <button
        type="button"
        onClick={() => expandable && setOpen((v) => !v)}
        className={`w-full flex items-start gap-2.5 py-2 text-left ${expandable ? 'hover:text-[#F5F5F5] cursor-pointer' : 'cursor-default'}`}
        aria-expanded={open}
      >
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#EAB308' }} />
        <span className="text-xs text-[#E4E4E7] leading-relaxed flex-1">{title}</span>
        {expandable && (
          <ChevronDown
            className={`w-3.5 h-3.5 text-[#EAB308] mt-0.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        )}
      </button>
      {expandable && open && (
        <p className="pl-4 pb-3 text-[11px] text-[#A1A1AA] leading-relaxed" data-testid="upgrade-item-benefit">
          {benefit}
        </p>
      )}
    </li>
  );
}

// ────────────────────────────────────────────────────────────────
// PackageChips — visual rendering of `look.tailor_notes.package_chips`.
// Shoes / ties / belts / vests / watches = colour-square swatch + label.
// Shirts = pattern-swatch tile (solid / pin-stripe / Bengal stripe /
// gingham / micro-check / Oxford / twill / tattersall) + label. Others
// fall back to a quiet bullet. Purely informational — no click action.
// ────────────────────────────────────────────────────────────────
function ShirtPatternSwatch({ pattern, color }) {
  // CSS-only pattern generation so we don't have to ship images.
  const base = color || '#ADD8E6';
  // Choose a sensible accent (a darker companion stripe) — preserve hue,
  // drop lightness. Falls back to a neutral charcoal for unknown inputs.
  const accent = '#1C2841';
  const swatchStyles = {
    solid:             { background: base },
    pin_stripe:        { background: `repeating-linear-gradient(90deg, ${base} 0 7px, ${accent} 7px 8px)` },
    bengal_stripe:     { background: `repeating-linear-gradient(90deg, ${base} 0 5px, ${accent} 5px 8px)` },
    university_stripe: { background: `repeating-linear-gradient(90deg, ${base} 0 4px, ${accent} 4px 9px)` },
    gingham:           { background: `repeating-linear-gradient(90deg, ${base} 0 4px, ${accent} 4px 8px), repeating-linear-gradient(0deg, ${base} 0 4px, ${accent} 4px 8px)`, backgroundBlendMode: 'multiply' },
    micro_check:       { background: `repeating-linear-gradient(90deg, ${base} 0 2px, ${accent} 2px 4px), repeating-linear-gradient(0deg, ${base} 0 2px, ${accent} 2px 4px)`, backgroundBlendMode: 'multiply' },
    tattersall:        { background: `repeating-linear-gradient(90deg, transparent 0 11px, ${accent} 11px 12px), repeating-linear-gradient(0deg, transparent 0 11px, ${accent} 11px 12px), ${base}` },
    oxford:            { background: `repeating-linear-gradient(45deg, ${base} 0 2px, rgba(0,0,0,0.08) 2px 3px), ${base}` },
    twill:             { background: `repeating-linear-gradient(45deg, ${base} 0 3px, rgba(0,0,0,0.10) 3px 4px)` },
  };
  const style = swatchStyles[pattern] || swatchStyles.solid;
  return (
    <span
      className="block w-7 h-7 border border-[#27272A] rounded-sm flex-shrink-0"
      style={style}
      aria-hidden="true"
    />
  );
}

function ColorSwatch({ hex }) {
  return (
    <span
      className="block w-7 h-7 border border-[#27272A] rounded-sm flex-shrink-0"
      style={{ backgroundColor: hex || '#1C2841' }}
      aria-hidden="true"
    />
  );
}

// ────────────────────────────────────────────────────────────────────────
// `parsePackageTextToChips` — best-effort regex parser that turns legacy
// `package: ["Crisp white cotton dress shirt", "Deep burgundy silk tie", ...]`
// text bullets into structured `package_chips` objects so the visual chip
// renderer works for EVERY look in the gallery, not just iter_49+ renders.
//
// Strategy:
//   1. Detect kind via keyword match (shirt / tie / bow tie / shoes / pocket
//      square / belt / trouser / vest). Defaults to 'other'.
//   2. Match against a curated COLOR_NAMES dictionary (≈40 menswear-relevant
//      colours) — picks the LONGEST match wins to disambiguate "navy" vs
//      "midnight navy".
//   3. For shirts, detect pattern keyword (stripe / gingham / oxford / etc.).
//
// Pure function, no LLM cost, runs synchronously on render.
// ────────────────────────────────────────────────────────────────────────
const COLOR_NAMES = {
  // Foundation neutrals
  'midnight navy':  '#0F1F3F', 'midnight':       '#0F1F3F',
  'navy':           '#1C2841', 'dark navy':      '#0F1F3F',
  'charcoal':       '#36454F', 'slate':          '#708090', 'slate grey': '#708090', 'slate gray': '#708090',
  'dove grey':      '#9098A1', 'dove gray':      '#9098A1',
  'mid grey':       '#7A7A7A', 'mid gray':       '#7A7A7A', 'grey': '#9098A1', 'gray': '#9098A1',
  'light grey':     '#B5B5B8', 'light gray':     '#B5B5B8',
  'black':          '#0B0B0B', 'jet black':      '#0A0A0A',
  // Whites + creams
  'white':          '#FFFFFF', 'crisp white':    '#FFFFFF', 'pure white':     '#FFFFFF',
  'cream':          '#F5F0E1', 'ivory':          '#F5F0E1', 'ecru':           '#E8DCC4',
  // Browns + earths
  'chocolate':      '#3B2F2F', 'dark brown':     '#3B2F2F', 'mid-brown':      '#7A4C2E', 'mid brown':      '#7A4C2E',
  'brown':          '#6F4E37', 'tobacco':        '#563C20', 'cognac':         '#9C5A2F',
  'tan':            '#D2B48C', 'camel':          '#C19A6B', 'caramel':        '#A87C50',
  'rust':           '#8B4513', 'terracotta':     '#C66B43',
  // Reds + burgundies
  'burgundy':       '#722F37', 'deep burgundy':  '#5C0A0A', 'oxblood':        '#4A1C22',
  'maroon':         '#5C2E2E', 'wine':           '#5C2E2E', 'crimson':        '#A0202E',
  'red':            '#A0202E',
  // Greens
  'forest green':   '#2C4A3A', 'forest':         '#2C4A3A', 'hunter green':   '#1A3A2A', 'hunter':         '#1A3A2A',
  'olive':          '#556B2F', 'olive drab':     '#556B2F', 'sage':           '#9CAF88',
  'emerald':        '#066B44', 'bottle green':   '#1F3A2E',
  // Blues
  'sky blue':       '#7CB9E8', 'light blue':     '#ADD8E6', 'powder blue':    '#B0CCDB',
  'royal blue':     '#1A3A6C', 'royal':          '#1A3A6C', 'french blue':    '#0072BB',
  'dusty blue':     '#7DA0B3', 'cobalt':         '#0047AB',
  'blue':           '#1A3A6C',
  // Yellows + pastels + pinks
  'gold':           '#C9A227', 'champagne':      '#D4B996', 'mustard':        '#D4A017',
  'lavender':       '#B5A4C2', 'lilac':          '#C8A2C8', 'plum':           '#5C2E5C',
  'pink':           '#E8C5C9', 'pale pink':      '#F2D6DA', 'dusty rose':     '#C9A0A4', 'blush':          '#E8C5C9',
  'peach':          '#FFCBA4', 'salmon':         '#E8917C',
  // Metallics + others
  'silver':         '#C0C0C8', 'bronze':         '#804A00', 'copper':         '#B87333',
  // Compound modifiers / plurals frequently used in the tailor notes
  'medium-grey':    '#7A7A7A', 'medium grey':    '#7A7A7A', 'medium gray':    '#7A7A7A',
  'charcoal grey':  '#36454F', 'charcoal gray':  '#36454F',
  'burnt orange':   '#CC5500', 'orange':         '#D97706',
  'mustard yellow': '#D4A017', 'butter yellow':  '#F0DC82',
};

function _hexForColorName(text) {
  if (!text) return null;
  const lower = String(text).toLowerCase();
  // Longest-name-wins so "midnight navy" beats "navy" inside the same string.
  const names = Object.keys(COLOR_NAMES).sort((a, b) => b.length - a.length);
  for (const n of names) {
    if (lower.includes(n)) return COLOR_NAMES[n];
  }
  return null;
}

function _kindForText(text) {
  const t = String(text || '').toLowerCase();
  if (/\bbow[\s-]?tie\b/.test(t)) return 'bow_tie';
  // Tie bar / tie clip / cuff links — group with pocket square as "Accessory".
  // Must be checked BEFORE the generic `tie` branch, otherwise "tie bar" maps
  // to the necktie kind.
  if (/\btie[\s-]?(bar|clip|tack|pin)\b/.test(t))   return 'accessory';
  if (/\bpocket\s+square\b/.test(t))               return 'accessory';
  if (/\b(cuff[\s-]?links?|cufflinks?|lapel\s+pin|tie\s+slide|boutonni[eè]re)\b/.test(t)) return 'accessory';
  // `necktie` must be checked BEFORE the generic `tie` branch since /\btie\b/
  // doesn't match inside the compound word "necktie".
  if (/\bnecktie\b/.test(t))                       return 'tie';
  if (/\btie\b/.test(t))                           return 'tie';
  // Plurals — `\b...\b` doesn't match across a word-final 's' so explicit `s?`.
  if (/\b(loafers?|oxfords?|derbys?|brogues?|chelsea|shoes?|boots?|monks?|wholecut)\b/.test(t)) return 'shoes';
  if (/\b(shirt|poplin|broadcloth|chambray|knit polo|polo)\b/.test(t)) return 'shirt';
  if (/\bsocks?\b/.test(t))                        return 'other';  // could be its own kind later
  if (/\bbelts?\b/.test(t))                        return 'belt';
  if (/\b(trousers?|pants?|chinos?|slacks)\b/.test(t)) return 'trouser';
  if (/\bwaistcoats?\b|\bvests?\b/.test(t))        return 'vest';
  if (/\bwatch(es)?\b/.test(t))                    return 'watch';
  return 'other';
}

const SHIRT_PATTERN_KEYWORDS = [
  // Order matters — most specific first
  ['bengal stripe',      'bengal_stripe'],
  ['university stripe',  'university_stripe'],
  ['pin stripe',         'pin_stripe'],
  ['pin-stripe',         'pin_stripe'],
  ['pinstripe',          'pin_stripe'],
  ['gingham',            'gingham'],
  ['micro-check',        'micro_check'],
  ['micro check',        'micro_check'],
  ['tattersall',         'tattersall'],
  ['oxford',             'oxford'],
  ['twill',              'twill'],
];

function _patternForText(text) {
  const t = String(text || '').toLowerCase();
  for (const [kw, pat] of SHIRT_PATTERN_KEYWORDS) {
    if (t.includes(kw)) return pat;
  }
  return 'solid';
}

function parsePackageTextToChips(textArray) {
  if (!Array.isArray(textArray)) return [];
  const chips = [];
  for (const raw of textArray.slice(0, 5)) {
    if (typeof raw !== 'string' || !raw.trim()) continue;
    const kind = _kindForText(raw);
    const color_hex = _hexForColorName(raw);
    if (kind === 'other' && !color_hex) {
      // Skip — not visually representable. The text bullet will still appear
      // if we render via the legacy text branch instead.
      continue;
    }
    chips.push({
      kind,
      color_hex: color_hex || '#1C2841',
      pattern: kind === 'shirt' ? _patternForText(raw) : 'solid',
      label: raw,
    });
  }
  return chips;
}

const CHIP_KIND_ICON = {
  // Lightweight emoji-free icon hints via shape — most chips read via
  // colour/pattern + label, so this is purely a kind hint.
  shirt: 'Shirt',
  tie: 'Tie',
  bow_tie: 'Bow Tie',
  shoes: 'Shoes',
  trouser: 'Trouser',
  // Tie bars, pocket squares, cufflinks, lapel pins and boutonnières all
  // share the "Accessory" badge — they're the small dress-finishing pieces.
  accessory: 'Accessory',
  // Legacy key — older saved chips may still arrive with kind='pocket_square'.
  // Map it to the same Accessory label so the UI stays consistent without a
  // data migration.
  pocket_square: 'Accessory',
  belt: 'Belt',
  watch: 'Watch',
  vest: 'Vest',
  other: '',
};

// ────────────────────────────────────────────────────────────────────
// Multi-palette suggestions for "This looks good with"
// Based on classic menswear references (Permanent Style, The Rake,
// He Spoke Style). For each chip we offer 2 ADDITIONAL palette options
// that work as substitutes for the primary recommendation — same
// accessory type, related hue family.
// ────────────────────────────────────────────────────────────────────
function _hexToHsl(hex) {
  if (typeof hex !== 'string' || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h = h * 60;
  }
  return { h, s, l };
}

// Classify a hex into a menswear-relevant hue family. Order matters —
// achromatic checks (white/grey/black) run first so off-whites and warm
// greys don't get misclassified as warm hues.
function _hueFamily(hex, label) {
  const hsl = _hexToHsl(hex);
  if (!hsl) return 'neutral';
  const { h, s, l } = hsl;
  const lower = String(label || '').toLowerCase();
  // Achromatic family
  if (s < 0.12) {
    if (l > 0.85) return 'white';
    if (l > 0.55) return 'silver';
    if (l > 0.30) return 'grey';
    return 'black';
  }
  // Warm earth tones beat blue/red label matches
  if (lower.includes('tan') || lower.includes('cognac') || lower.includes('camel')) return 'tan';
  if (lower.includes('brown') || lower.includes('espresso') || lower.includes('chocolate')) return 'brown';
  if (lower.includes('oxblood') || lower.includes('burgundy') || lower.includes('wine') || lower.includes('claret')) return 'burgundy';
  if (lower.includes('forest') || lower.includes('hunter') || lower.includes('olive') || lower.includes('sage')) return 'forest';
  if (lower.includes('navy') || lower.includes('midnight')) return 'navy';
  if (lower.includes('pink') || lower.includes('blush') || lower.includes('salmon') || lower.includes('rose')) return 'pink';
  if (lower.includes('lavender') || lower.includes('lilac') || lower.includes('mauve') || lower.includes('purple')) return 'lavender';
  if (lower.includes('gold') || lower.includes('champagne') || lower.includes('mustard') || lower.includes('ochre')) return 'gold';
  // Geometric hue bucket fallback
  if (h < 15 || h >= 345) return l < 0.30 ? 'burgundy' : 'pink';     // red family
  if (h < 35)  return 'brown';                                       // red-orange / warm browns
  if (h < 50)  return 'tan';                                         // orange / tan
  if (h < 65)  return l > 0.55 ? 'gold' : 'brown';                   // yellow / khaki
  if (h < 165) return 'forest';                                      // green family
  if (h < 260) return l > 0.70 ? 'light_blue' : (l < 0.30 ? 'navy' : 'blue');
  if (h < 295) return 'lavender';                                    // purple family
  return 'pink';                                                     // pinks / magentas
}

// Research-backed palette alternates — 2 alternatives per family per kind.
// Sourced from classic menswear style references; intentionally narrow so
// every alternate stays in the same accessory category and reads as a
// genuine swap, not a wild left-field option.
const PALETTE_ALTERNATIVES = {
  tie: {
    burgundy:   ['#5D1A1D', '#8B2535'],
    navy:       ['#0F1B2D', '#2C3E5E'],
    blue:       ['#1F4E79', '#274E70'],
    light_blue: ['#7CAED0', '#4D88B8'],
    forest:     ['#1E3F1E', '#365B36'],
    silver:     ['#9A9A9A', '#C0C0C0'],
    grey:       ['#525252', '#7A7A7A'],
    gold:       ['#A88A4A', '#D4B580'],
    pink:       ['#C28A8A', '#E5B5B5'],
    lavender:   ['#8A82AC', '#B7A8D9'],
    brown:      ['#5D3920', '#3D2613'],
    tan:        ['#9C7A4D', '#C39A6F'],
    white:      ['#F0E9D8', '#E8E0CB'],
    black:      ['#1A1A1A', '#0B0B0B'],
    neutral:    [],
  },
  bow_tie: {
    burgundy:   ['#5D1A1D', '#8B2535'],
    navy:       ['#0F1B2D', '#2C3E5E'],
    forest:     ['#1E3F1E', '#365B36'],
    silver:     ['#9A9A9A', '#C0C0C0'],
    black:      ['#1A1A1A', '#0B0B0B'],
    white:      ['#F5F1E8', '#E8E0CB'],
    neutral:    [],
  },
  // Pocket squares, tie bars, cufflinks, lapel pins, boutonnières
  accessory: {
    burgundy:   ['#5D1A1D', '#A2475A'],
    navy:       ['#0F1B2D', '#3A5378'],
    forest:     ['#1E3F1E', '#5A7A4D'],
    silver:     ['#C0C0C0', '#9A9A9A'],
    gold:       ['#C2A878', '#A88A4A'],
    pink:       ['#E5B5B5', '#D9A6A0'],
    lavender:   ['#B7A8D9', '#8A82AC'],
    white:      ['#F8F4EB', '#FFFFFF'],
    grey:       ['#7A7A7A', '#525252'],
    brown:      ['#5D3920', '#7B4F2B'],
    tan:        ['#C39A6F', '#9C7A4D'],
    black:      ['#1A1A1A', '#0B0B0B'],
    neutral:    [],
  },
  pocket_square: null, // alias — resolved at lookup time to 'accessory'
  shirt: {
    white:      ['#FFFFFF', '#F5F1E8'],   // bright / ivory
    light_blue: ['#C9DFEC', '#9DBED0'],   // sky / French blue
    pink:       ['#F6C7CE', '#E8B5C0'],
    lavender:   ['#D6CFEA', '#B7A8D9'],
    blue:       ['#274E70', '#3A5378'],
    silver:     ['#E0E0E0', '#C8C8C8'],
    neutral:    [],
  },
  shoes: {
    black:      ['#0B0B0B', '#1A0E0A'],   // jet black / espresso black
    brown:      ['#5D3920', '#3D2613'],   // mid brown / dark chocolate
    burgundy:   ['#5C1A1A', '#722F37'],   // oxblood / burgundy calf
    tan:        ['#A0522D', '#B86F4B'],   // cognac / tan calf
    cognac:     ['#9C5A3C', '#B86F4B'],
    neutral:    ['#3D2613', '#0B0B0B'],   // sensible default: dark brown + black
  },
  belt: {
    // Belt should match shoes — reuse the shoes palette.
    black:    ['#0B0B0B', '#1A0E0A'],
    brown:    ['#5D3920', '#3D2613'],
    burgundy: ['#5C1A1A', '#722F37'],
    tan:      ['#A0522D', '#B86F4B'],
    cognac:   ['#9C5A3C', '#B86F4B'],
    neutral:  ['#3D2613', '#0B0B0B'],
  },
  trouser: {
    navy:    ['#1C2841', '#0F1B2D'],
    grey:    ['#525252', '#7A7A7A'],
    silver:  ['#9A9A9A', '#C0C0C0'],
    brown:   ['#5D3920', '#7B4F2B'],
    tan:     ['#9C7A4D', '#C39A6F'],
    black:   ['#1A1A1A', '#0B0B0B'],
    neutral: [],
  },
  vest: {
    navy:     ['#1C2841', '#0F1B2D'],
    grey:     ['#525252', '#7A7A7A'],
    burgundy: ['#5D1A1D', '#722F37'],
    forest:   ['#1E3F1E', '#365B36'],
    black:    ['#1A1A1A', '#0B0B0B'],
    neutral:  [],
  },
  watch: {
    silver:  ['#C0C0C0', '#9A9A9A'],
    gold:    ['#C2A878', '#A88A4A'],
    black:   ['#1A1A1A', '#0B0B0B'],
    brown:   ['#5D3920', '#3D2613'],
    neutral: [],
  },
  other: {},
};

function getAlternativePalette(kind, hex, label) {
  const lookupKind = (kind === 'pocket_square') ? 'accessory' : kind;
  const family = _hueFamily(hex, label);
  const table = PALETTE_ALTERNATIVES[lookupKind] || {};
  let alts = table[family] || table.neutral || [];
  // De-dupe versus the base swatch — no point showing the same colour twice.
  return alts.filter(a => a.toLowerCase() !== String(hex || '').toLowerCase()).slice(0, 2);
}

function PackageChips({ chips }) {
  return (
    <ul className="space-y-2.5" data-testid="package-chips">
      {chips.map((chip, idx) => {
        const kind = (chip.kind || 'other').toLowerCase();
        const isShirt = kind === 'shirt';
        const label = chip.label || `${chip.color_name || ''} ${kind}`.trim();
        const kindBadge = CHIP_KIND_ICON[kind] || '';
        // ── Hex / label reconciliation ───────────────────────────
        // Gemini occasionally picks a `color_hex` that doesn't match the
        // colour word in its own `label` (e.g. label says "burgundy" but
        // hex came back as pink #A2475A). Whenever the label contains a
        // recognised menswear colour name, override the hex to the
        // canonical menswear value so the swatch ALWAYS matches the
        // description. Falls back to the original hex if no match.
        // BELT-MATCH-SHOES (Feb 2026): if upstream tagged this chip with
        // `_belt_match_hex` (set when both a belt and shoes chip exist),
        // pin the belt's effective swatch to that value so the menswear
        // rule "belt colour = shoe colour" is always honoured.
        const swatchHex = chip._belt_match_hex
          || _hexForColorName(label)
          || chip.color_hex
          || '#1C2841';
        // 0-2 alternative palette swatches (classic menswear pairing
        // alternates) — surfaced beneath the label so the consultant can
        // see other colour options for the same accessory at a glance.
        const alternates = getAlternativePalette(kind, swatchHex, label);
        return (
          <li
            key={idx}
            className="flex items-start gap-3"
            data-testid={`package-chip-${kind}-${idx}`}
            title={label}
          >
            {isShirt
              ? <ShirtPatternSwatch pattern={chip.pattern || 'solid'} color={swatchHex} />
              : <ColorSwatch hex={swatchHex} />
            }
            <div className="flex flex-col min-w-0 flex-1">
              {kindBadge && (
                <span className="text-[9px] uppercase tracking-wider text-[#71717A] leading-none mb-0.5">
                  {kindBadge}
                </span>
              )}
              <span className="text-xs text-[#E4E4E7] leading-snug">
                {label}
              </span>
              {alternates.length > 0 && (
                <div
                  className="flex items-center gap-1.5 mt-1.5"
                  data-testid={`package-chip-${kind}-${idx}-alternates`}
                  aria-label="Alternative palette options"
                >
                  <span className="text-[8px] uppercase tracking-[0.15em] text-[#52525B] leading-none">
                    or
                  </span>
                  {alternates.map((alt, ai) => (
                    <span
                      key={ai}
                      className="w-3 h-3 border border-[#3F3F46] shadow-[0_1px_2px_rgba(0,0,0,0.4)]"
                      style={{ backgroundColor: alt }}
                      title={alt.toUpperCase()}
                    />
                  ))}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ColourPicker({ colors, value, onChange, testId, isStarred }) {
  return (
    <div className="flex flex-wrap gap-2">
      {colors.map(c => {
        const starred = typeof isStarred === 'function' ? isStarred(c.value) : false;
        return (
          <button
            key={c.value}
            onClick={() => onChange(c.value)}
            className={`relative w-7 h-7 border-2 transition-all ${value === c.value ? 'border-[#F5F5F5] scale-110' : 'border-transparent hover:border-[#52525B]'}`}
            style={{ backgroundColor: c.value }}
            title={starred ? `${c.name} · ★ recommended for this client` : c.name}
            data-testid={`${testId}-${c.name}`}
          >
            {starred && (
              <span
                className="absolute -top-1 -right-1 w-3 h-3 flex items-center justify-center rounded-full bg-[#0A0A0C] border border-[#C2A878]"
                style={{ pointerEvents: 'none' }}
                aria-label="Recommended"
              >
                <span className="text-[7px] leading-none text-[#C2A878]">★</span>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function OptionPills({ options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)}
          className={`px-3 py-1 text-[10px] uppercase tracking-wider border transition-all ${value === o.value ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function CustomizeModal({ look, open, onClose, onRegenerate, isRegenerating, colorMatcher, isDemoStore, storeSettings, onDesign, isMasterAdmin }) {
  const [suitColor, setSuitColor] = useState(look?.combo?.suit || '#1C2841');
  const [shirtColor, setShirtColor] = useState(SHIRT_COLORS.find(c => c.name === look?.combo?.shirt)?.value || '#FFFFFF');
  const [pantColor, setPantColor] = useState(look?.combo?.pant || '#36454F');
  const [shoeType, setShoeType] = useState(look?.combo?.shoe || 'oxford');
  const [shoeColor, setShoeColor] = useState(SHOE_COLORS.find(c => c.name === look?.combo?.shoeColor)?.value || '#0B0B0B');
  const [pattern, setPattern] = useState('solid');
  const [shirtPattern, setShirtPattern] = useState('solid');
  const [lapel, setLapel] = useState('notch');
  // Iter 130k — satin lapel toggle. Only shown when the current look
  // reads as a tuxedo / smoking jacket / dinner jacket. Auto-defaults
  // to `true` for those garments so the initial state matches how
  // tuxedos are actually built; the user can uncheck to render a
  // non-satin variant (e.g. a matte-wool tuxedo experiment).
  const _lookGarmentId = String(look?.garment_id || look?.garment_type || '').toLowerCase();
  const _lookGarmentName = String(look?.garment_name || '').toLowerCase();
  const _lookFabricNote = String(look?.fabric_note || '').toLowerCase();
  const isTuxedoLook = (
    _lookGarmentId === 'tuxedo' || _lookGarmentId.includes('tux') ||
    /\b(tuxedo|dinner jacket|smoking jacket|black[\s-]?tie)\b/.test(
      `${_lookGarmentName} ${_lookFabricNote}`
    )
  );
  // Feb 2026 — shirt-category flags. Dress shirts get collar + cuff +
  // pants edits; casual shirts get pant edit only. Detect via
  // `look.category` (shirts_casual_shirts, shirts) and garment_id
  // (dress_shirt, casual_shirt, polo).
  const _lookCategory = String(look?.category || '').toLowerCase();
  const isShirtLook = _lookCategory.startsWith('shirts');
  const isCasualShirtLook = (
    isShirtLook && (
      _lookCategory.includes('casual') ||
      _lookGarmentId === 'casual_shirt' ||
      _lookGarmentId.includes('polo') ||
      _lookGarmentId.includes('tee') ||
      _lookGarmentId.includes('henley')
    )
  );
  const isDressShirtLook = isShirtLook && !isCasualShirtLook;
  const [satinLapel, setSatinLapel] = useState(isTuxedoLook);
  const [buttonCount, setButtonCount] = useState('2');
  const [buttonStyle, setButtonStyle] = useState('matching');
  const [tieType, setTieType] = useState('none');
  const [tieColor, setTieColor] = useState('#1C2841');
  const [trouserStyleCust, setTrouserStyleCust] = useState('flat_front');
  const [customizeOpen, setCustomizeOpen] = useState(false);  // collapsed by default
  // Iter 109 v6 — imperative ref on the zoom container so we can reset
  // the inline `transform: scale(...)` on the <img> child whenever the
  // active image changes (variant swap, regen finished, modal re-open).
  // Fixes the "stuck-zoomed on edit" glitch — the old code only reset
  // on `onMouseLeave`, so an image that swapped in mid-hover kept the
  // 2.25× scale and jumped when the user next moved the mouse.
  const imgContainerRef = useRef(null);
  // Track the most recently picked pant chip so the active state of the
  // grid stays in sync with what the user clicked, even while the Gemini
  // re-render is in flight.
  const [pendingPantName, setPendingPantName] = useState(null);
  // Same idea for the button-colour chip row.
  const [pendingButtonName, setPendingButtonName] = useState(null);
  // ── Gate the Recolour Pants section ───────────────────────────────
  // Per user spec: surface the pant recolour chips for any Business
  // Casual look (top-level scenarios AND `bc_*` subcategories, plus the
  // BC Style Inspirations row) AND for Custom Fabric renders that
  // happen to be sports coats / blazers. Iter 129 — also enabled for
  // OUTERWEAR (overcoats, topcoats, trench, peacoat, car coat, mandarin
  // coat) because those looks are rendered OPEN over trousers — the
  // trouser swap reads cleanly, jacket-crop reference locks the coat.
  // Suits + wedding + shirts still hide it because the trouser fabric
  // there is by definition the same cloth as the jacket.
  const _showPantRecolour = (() => {
    const cat = (look?.category || '').toLowerCase();
    const isBC = (
      cat === 'business_casual'
      || cat === 'inspiration_business_casual'
      || cat.endsWith('_business_casual')
      || cat.startsWith('bc_')
    );
    const isCustomFabric = cat === 'custom_fabric';
    const isOuterwear = (
      cat === 'outerwear'
      || cat === 'inspiration_outerwear'
      || cat.startsWith('outerwear_')
      || cat.startsWith('inspiration_outerwear_')
    );
    const gid = (look?.combo?.garment_id || look?.garment_id || '').toLowerCase();
    const gname = (look?.combo?.garment || look?.garment || look?.label || '').toLowerCase();
    const isOuterwearGarment = ['peacoat', 'overcoat', 'topcoat', 'trench', 'car_coat', 'mandarin_coat'].some(k => gid.includes(k))
      || /\b(overcoat|topcoat|trench|peacoat|car coat|mandarin coat)\b/.test(gname);
    if (isOuterwear || isOuterwearGarment) return true;
    // Feb 2026 — shirts (both dress and casual) also expose the pant
    // recolour row. Per user spec: casual shirts get ONLY a pant edit;
    // dress shirts get collar + cuff + pants. Both need this row.
    if (isShirtLook) return true;
    if (!isBC && !isCustomFabric) return false;
    if (gid.includes('blazer')) return true;
    return gname.includes('sports coat') || gname.includes('sport coat') || gname.includes('blazer');
  })();
  // Recolour section collapsible (default collapsed so it doesn't
  // hijack the consultant's attention on every modal open).
  const [recolourOpen, setRecolourOpen] = useState(false);
  const [buttonsOpen, setButtonsOpen] = useState(false);
  // Feb 2026 — pending trouser cut chip. Mirrors the pending-name
  // pattern used by pant colour / button / collar / cuff editors so
  // the active chip stays highlighted while Gemini is in flight.
  const [pendingTrouserCut, setPendingTrouserCut] = useState(null);
  // Feb 2026 — Restructure · Trousers & Shoes multi-select state.
  // Mirrors the vest restructure inside the Silhouette panel:
  // pleats (none/single/double, single-select), cuffs (bool),
  // waistband (belt vs side_tabs, mutually exclusive), belt colour,
  // shoe style, and shoe colour. Apply button composes a `+`-joined
  // `trouser_style` token string PLUS separate `shoe_type` /
  // `shoe_color` fields so the backend structural_only_lock sees
  // exactly ONE edit — pose, face and framing stay locked.
  const [trouserRestructureOpen, setTrouserRestructureOpen] = useState(false);
  // Sep 2026 (user request — "for overcoat add a restructure option where
  // you can extend the overcoat to mid calf") — instant-apply chip pair
  // on overcoat cards. Fires a `__coatLengthOnly` edit through Dashboard
  // → backend, where a dedicated `is_coat_length_only_edit` lock
  // redraws only the hem and keeps face / pose / fabric / buttons /
  // collar / cuffs / backdrop / base outfit pixel-identical.
  const [coatLengthOpen, setCoatLengthOpen] = useState(false);
  const [pendingCoatLength, setPendingCoatLength] = useState(null);
  const [trPleats,   setTrPleats]   = useState(null);   // 'none' | 'single' | 'double' | null
  const [trCuffs,    setTrCuffs]    = useState(false);
  // Waistband is a 3-way exclusive: null (no change from default),
  // 'belt' (belt loops + leather belt), or 'side_tabs' (adjusters
  // replace belt loops — NO belt loops, NO belt).
  const [trWaist,     setTrWaist]     = useState(null); // null | 'belt' | 'side_tabs'
  const [trBeltColor, setTrBeltColor] = useState(null); // e.g. 'Black' | 'Dark Brown' | 'Cognac' | ...
  // Iter 154 — shoe restructure grouped with trousers per user spec.
  // Shoe style picker (Oxford / Derby / Loafer / Monk / Chelsea /
  // Sneaker). Shoe colour is *linked* to belt colour by default:
  // when the tailor picks a belt colour we auto-set the shoe colour
  // to the same value so belt + shoes always share the same leather
  // family (classic menswear rule). The tailor can still override
  // the shoe colour independently after that.
  const [trShoeType,  setTrShoeType]  = useState(null); // 'oxford' | 'derby' | 'loafer' | 'monk_strap' | 'chelsea_boot' | 'sneaker' | null
  const [trShoeColor, setTrShoeColor] = useState(null); // menswear leather colour name
  // Iter 162 — DIRTY FLAG for the six restructure controls. `true` only
  // when the tailor has ACTIVELY touched one of the pickers since the
  // last hydration (modal open, or look prop update from parent). After
  // iter 160 introduced hydration of these six slots from
  // `look.applied_edits`, the previous `hasRestructurePending` check
  // (any of the six is non-null) became meaningless — it was always
  // true after any prior restructure. That caused every subsequent
  // pant chip click to bundle the restructure into
  // `__pantWithRestructure`, which pushed the edit through the backend
  // structural bundle path (weaker jacket preservation) instead of the
  // dedicated pant_only_lock. `restructureDirty` cleanly separates
  // "hydrated state matching the current render" from "user actually
  // wants to change the restructure".
  const [restructureDirty, setRestructureDirty] = useState(false);
  // Feb 2026 — tuxedo trouser satin side stripe (galon). Tuxedo-only
  // pill inside the Silhouette Restructure panel; fires a
  // trouser-only edit that repaints the outside seam with a black
  // silk-satin braid — the classic black-tie side stripe. Stays
  // sticky-highlighted while Gemini re-renders.
  const [pendingTuxTrouser, setPendingTuxTrouser] = useState(null);
  // Trouser cut row surfaces ONLY for looks where the trousers are a
  // separate garment from the top and can be meaningfully re-cut
  // (business-casual sports coats / blazers + custom-fabric sport
  // coat renders). Matching-suits, wedding, tuxedo, shirts, and
  // outerwear standalones hide the row per user spec — those
  // silhouettes either share trouser cloth with the jacket (suits)
  // or don't render a full trouser leg (outerwear-only crops).
  const _showTrouserCut = (() => {
    if (!look?.image) return false;
    const cat = (look?.category || '').toLowerCase();
    const isBC = (
      cat === 'business_casual'
      || cat === 'inspiration_business_casual'
      || cat.endsWith('_business_casual')
      || cat.startsWith('bc_')
    );
    if (isBC) return true;
    // Custom-fabric sport-coat / blazer renders — the trouser is a
    // dedicated garment separate from the jacket cloth, so the cut
    // swap reads cleanly.
    if (cat === 'custom_fabric') {
      const gid = (look?.combo?.garment_id || look?.garment_id || '').toLowerCase();
      const gname = (look?.combo?.garment || look?.garment || look?.label || '').toLowerCase();
      if (gid.includes('blazer')) return true;
      if (gname.includes('sports coat') || gname.includes('sport coat') || gname.includes('blazer')) return true;
    }
    return false;
  })();
  // Iter 88 — tie-colour quick-edit. Mirrors the button editor's
  // collapsible + pending-name pattern. Only surfaces when the look
  // is a full-suit (or wedding) WITH a tie — chip detection via
  // look.tailor_notes.package_chips kind='tie' OR an explicit
  // look.tie_type / look.combo.accessory hint.
  const [tieOpen, setTieOpen] = useState(false);
  const [pendingTieName, setPendingTieName] = useState(null);
  // Iter 115 — Silhouette dropdown. New multi-choice editor below the
  // Tie recolour that lets the user restructure the existing render:
  // SB↔DB, open/close jacket, add/remove vest. Multiple choices can be
  // stacked in one Apply-button pass. `null` = "leave as-is".
  const [silhouetteOpen, setSilhouetteOpen] = useState(false);
  const [silhJacketStyle, setSilhJacketStyle] = useState(null);   // 'single_breasted' | 'double_breasted' | null
  const [silhJacketState, setSilhJacketState] = useState(null);   // 'open' | 'closed' | null
  const [silhVest,         setSilhVest]         = useState(null); // 'with' | 'without' | null
  const [silhLapel,        setSilhLapel]        = useState(null); // 'satin_shawl' | 'satin_peak' | null
  // Feb 2026 — vest customisation surfaced only when the user picks
  // "Add vest" in the Silhouette Restructure panel. Lets the tailor
  // choose SB vs DB, buttons, and vest lapel style — matches the
  // Custom-Fabric-Questionnaire vest section.
  const [silhVestStyle,    setSilhVestStyle]    = useState(null); // 'single_breasted' | 'double_breasted' | null
  const [silhVestButtons,  setSilhVestButtons]  = useState(null); // '4' | '5' | '6' | '4x2' | '6x2' | null
  const [silhVestLapel,    setSilhVestLapel]    = useState(null); // 'none' | 'notch' | 'peak' | 'shawl' | null
  // Feb 2026 — tuxedo bowtie ↔ tie converter lives inside the
  // silhouette restructure panel. Fires an edit with `tie_type` set
  // to 'bow_tie' or 'silk_solid' (mapped to bow tie / long necktie
  // by the backend humaniser). Only surfaced for tuxedo looks.
  const [silhTieForm,      setSilhTieForm]      = useState(null); // 'bow_tie' | 'silk_solid' | null
  // Feb 2026 — dress-shirt collar / cuff quick edits. Pending name
  // tracks the active chip while the Gemini call is in flight.
  const [pendingCollarName, setPendingCollarName] = useState(null);
  const [pendingCuffName,   setPendingCuffName]   = useState(null);
  const [collarOpen, setCollarOpen] = useState(false);
  const [cuffOpen,   setCuffOpen]   = useState(false);
  const _showSilhouetteEditor = (() => {
    if (!look?.image) return false;
    const cat = (look?.category || '').toLowerCase();
    if (cat.startsWith('shirts') || cat.startsWith('inspiration_shirts')) return false;
    if (cat.startsWith('outerwear') || cat.startsWith('inspiration_outerwear')) return false;
    return true;
  })();
  // Sep 2026 (user request) — Coat-length restructure gate. Surfaces
  // ONLY on overcoat / topcoat / Chesterfield / polo / Guards outerwear
  // looks, where an "Extend to mid-calf" toggle makes real tailoring
  // sense. Car coats, peacoats and trench coats have a defined length
  // in their silhouette DNA (hip / mid-thigh / knee w/ storm flap)
  // so we DON'T offer the mid-calf option there.
  //
  // Sep 2026 (user follow-up — "add these options on custom fabric as
  // well for overcoats") — extended to also fire for `custom_fabric`
  // category renders when the tailor chose an overcoat silhouette
  // during the custom-fabric flow. Garment gate still restricts to
  // overcoat / topcoat / Chesterfield / polo / Guards so the option
  // never surfaces on custom-fabric suits, blazers or shirts.
  const _showCoatLengthEditor = (() => {
    if (!look?.image) return false;
    const cat = (look?.category || '').toLowerCase();
    const gid = (look?.garment_id || look?.combo?.garment_id || '').toLowerCase();
    const gname = (look?.garment || look?.combo?.garment || look?.label || '').toLowerCase();
    const isOuterwearCat = (
      cat.startsWith('outerwear')
      || cat.startsWith('inspiration_outerwear')
      || cat === 'custom_fabric'
    );
    const isOvercoatGarment = (
      gid === 'overcoat'
      || /\b(overcoat|topcoat|chesterfield|polo coat|guards)\b/.test(gname)
    );
    return isOuterwearCat && isOvercoatGarment;
  })();
  const _showTieRecolour = (() => {
    if (!look?.image) return false;
    // Feb 2026 — TUXEDO: the general tie-recolour row is REPLACED by
    // the tuxedo-only Bowtie↔Tie converter inside the Silhouette
    // Restructure panel (see below). Hide the colour row here so the
    // tuxedo card only exposes the tuxedo-appropriate controls.
    if (isTuxedoLook) return false;
    const cat = (look?.category || '').toLowerCase();
    // Hide on categories that never carry neckwear (BC / shirts /
    // outerwear standalones / solo-blazer shirts).
    // NOTE (Feb 2026): `custom_fabric` was previously hard-excluded
    // here, which blocked the tie editor on custom-fabric SUITS even
    // when the render clearly featured a tie. Removed — custom_fabric
    // now falls through to the chip / narrative / explicit-tie_type
    // detection below, so custom-fabric suits with a tie get the
    // editor while custom-fabric shirts / sports coats / standalone
    // blazers (which carry no tie in their narrative) still hide it.
    const noTieCats = ['business_casual', 'inspiration_business_casual', 'shirts', 'inspiration_shirts', 'outerwear', 'inspiration_outerwear'];
    if (noTieCats.some(c => cat === c || cat.startsWith(c + '_') || cat.startsWith('bc_'))) return false;
    // Explicit opt-out — a tailor / combo author marked tie_type='none'.
    // Honour it before the default-true fallback below.
    if (look?.tie_type === 'none' || look?.combo?.tie_type === 'none') return false;
    if ((look?.combo?.accessory || '').toLowerCase() === 'none') return false;
    // Default-true for tailored full-suit / wedding categories — these
    // Essencials Auto-gens (Tom Ford, Brunello, Pinstripes, Windowpanes,
    // Formal, Patterned, plus their inspiration_ variants and wedding
    // looks) virtually ALWAYS carry a tie in the rendered image, even
    // though legacy looks (created before tie metadata was tracked)
    // have no positive signal on the document. Iter 88 follow-up — the
    // previous strict gate missed every one of them; this fallback
    // catches them while still respecting the noTieCats blacklist + the
    // explicit tie_type='none' opt-out above.
    const tieCats = ['full_suits', 'inspiration_full_suits', 'wedding', 'inspiration_wedding', 'formal', 'inspiration_formal'];
    if (tieCats.some(c => cat === c || cat.startsWith(c + '_'))) return true;
    // Custom-fabric SUITS — user feedback Feb 2026. A custom_fabric
    // look can be a suit, a sports coat, a blazer or a shirt. Show
    // the tie editor by default — the Custom Fabric upload flow
    // renders a full suit + tie by default — UNLESS the garment is
    // explicitly a non-tie type (shirt / sports coat / blazer / polo
    // / cardigan / outerwear, all of which never carry a tie).
    //
    // Iter 91 follow-up — demo-seed and live-upload Custom Fabric
    // looks store their garment hint in different fields. We union
    // every plausible source (root + variants) into a single haystack
    // and look for explicit NON-SUIT signals to suppress the row.
    const _variants = Array.isArray(look?.variants) ? look.variants : [];
    if (cat === 'custom_fabric') {
      const gnameParts = [
        look?.combo?.garment_id,
        look?.garment_id,
        look?.combo?.garment,
        look?.garment,
        look?.garment_name,
        look?.garment_type,
        look?.label,
        look?.tailor_notes?.description,
        ..._variants.map(v => v?.combo?.garment_id),
        ..._variants.map(v => v?.garment_id),
        ..._variants.map(v => v?.combo?.garment),
        ..._variants.map(v => v?.garment),
        ..._variants.map(v => v?.garment_name),
        ..._variants.map(v => v?.garment_type),
        ..._variants.map(v => v?.label),
      ];
      const hay = gnameParts.filter(Boolean).join(' ').toLowerCase();
      // Explicit non-tie garments — suppress the tie row.
      const isNonTieGarment = (
        /\bshirt\b/.test(hay)
        || /\bpolo\b/.test(hay)
        || /\bcardigan\b/.test(hay)
        || /\bsweater\b/.test(hay)
        || /\bsport[\s-]?coat\b/.test(hay)
        || /\bsports[\s-]?coat\b/.test(hay)
        || /\bblazer\b/.test(hay)
        || /\bouterwear\b/.test(hay)
        || /\bovercoat\b/.test(hay)
        || /\btrench\b/.test(hay)
        || /\bpeacoat\b/.test(hay)
        || /\bjeans\b/.test(hay)
        || /\bchinos?\b/.test(hay)
        || /\btrousers\b/.test(hay)
      );
      if (!isNonTieGarment) return true;
    }
    // Fallback positive-signal sniff for anything else (custom combos
    // a tailor builds in the future). Same checks as before — but
    // also union the per-variant tailor_notes / combo so custom_fabric
    // looks (metadata stored on variants[0]) and recolour-snapshots
    // both surface correctly.
    const _allChips = [
      ...(look?.tailor_notes?.package_chips || []),
      ..._variants.flatMap(v => v?.tailor_notes?.package_chips || []),
    ];
    if (_allChips.some(c => ['tie', 'bow_tie'].includes((c.kind || '').toLowerCase()))) return true;
    const _accStrings = [
      look?.combo?.accessory,
      look?.accessory,
      ..._variants.map(v => v?.combo?.accessory),
      ..._variants.map(v => v?.accessory),
    ].filter(Boolean).join(' ').toLowerCase();
    if (_accStrings.includes('tie')) return true;
    // Free-text fuzzy-match — "look_good_with" or "look_essentials"
    // narrative arrays often mention "silk tie" / "bow tie" even when
    // no structured chip exists. Union root + variants.
    const narratives = [
      ...(look?.tailor_notes?.look_good_with || []),
      ...(look?.tailor_notes?.look_essentials || []),
      ..._variants.flatMap(v => v?.tailor_notes?.look_good_with || []),
      ..._variants.flatMap(v => v?.tailor_notes?.look_essentials || []),
    ].join(' ').toLowerCase();
    if (/\b(tie|bow tie|silk tie|grenadine|four-in-hand|windsor)\b/.test(narratives)) return true;
    if (look?.tie_type && look.tie_type !== 'none') return true;
    if (look?.combo?.tie_type && look.combo.tie_type !== 'none') return true;
    if (_variants.some(v => v?.tie_type && v.tie_type !== 'none')) return true;
    if (_variants.some(v => v?.combo?.tie_type && v.combo.tie_type !== 'none')) return true;
    return false;
  })();
  // Buttons recolour applies universally per user spec — every look
  // has visible buttons somewhere (jacket placket, sleeve cuffs,
  // shirt placket, coat front), and the consultant should be able to
  // tweak the material everywhere.
  // Feb 2026 EXCEPTION — shirts (both dress and casual) explicitly
  // hide the button chip row per user spec: casual shirts only get a
  // pants edit; dress shirts get collar + cuff + pants edits.
  const _showButtonRecolour = !!look?.image && !isShirtLook;
  // Variant carousel state — mirrors the LookCard. Index 0 = latest;
  // 1..n = older snapshots in `look.variants`, newest first. Resets to
  // 0 whenever a new render lands so the consultant sees the freshest
  // version first; consultant can step back manually with the arrows.
  const [variantIdx, setVariantIdx] = useState(0);
  useEffect(() => {
    setVariantIdx(Math.min(Math.max(Number(look?._focusVariantIdx) || 0, 0),
      Array.isArray(look?.variants) ? look.variants.length : 0));
  }, [look?.id, look?._focusToken]);
  const variants = Array.isArray(look?.variants) ? look.variants : [];
  const variantCount = 1 + variants.length;
  const activeImage = variantIdx === 0 ? look?.image : variants[variantIdx - 1]?.image;
  const activeLabel = variantIdx === 0 ? 'Latest' : variants[variantIdx - 1]?.label || `Prev ${variantIdx}`;

  // Iter 109 v6 — Reset the img zoom-transform whenever:
  //  • the active image swaps (variant change or new render lands)
  //  • the modal re-opens for a different look
  //  • a regeneration begins (isRegenerating flips true)
  // Without this reset, the img stays visually scaled 2.25× until the
  // user moves the mouse OUT and BACK IN, which reads as a "zoom
  // glitch" — the image jumps when they next interact.
  useEffect(() => {
    const el = imgContainerRef.current;
    if (!el) return;
    const img = el.querySelector('img');
    if (!img) return;
    img.style.transform = 'scale(1)';
    img.style.transformOrigin = 'center center';
  }, [activeImage, look?.id, open, isRegenerating]);

  React.useEffect(() => {
    if (look?.combo) {
      setSuitColor(look.combo.suit || '#1C2841');
      setShirtColor(SHIRT_COLORS.find(c => c.name === look.combo.shirt)?.value || '#FFFFFF');
      setPantColor(look.combo.pant || look.combo.suit || '#36454F');
      setShoeType(look.combo.shoe || 'oxford');
      setShoeColor(SHOE_COLORS.find(c => c.name === look.combo.shoeColor)?.value || '#0B0B0B');
    }
    setPendingPantName(null);
    setPendingButtonName(null);
    setPendingTrouserCut(null);
    setPendingTuxTrouser(null);
    // Iter 160 — CUMULATIVE-EDIT HYDRATION. When the look doc carries
    // an `applied_edits` payload (persisted by /api/edit-look) we
    // hydrate the six restructure state slots from it so the UI
    // reflects the current stack of applied attributes. Prevents the
    // Chelsea-boots + oxblood-belt from visually resetting to null
    // after the tailor changes the pant colour. Legacy looks without
    // `applied_edits` fall back to the null defaults.
    const applied = (look && typeof look.applied_edits === 'object' && look.applied_edits) || {};
    // trouser_style composite parse — `+`-separated tokens with an
    // optional `belt:<colour>` inline. Same shape as the composite
    // the Apply button emits, so we round-trip cleanly.
    const trouserTokens = typeof applied.trouser_style === 'string'
      ? applied.trouser_style.split('+').map(t => t.trim()).filter(Boolean)
      : [];
    const hasSinglePleat = trouserTokens.includes('single_pleat');
    const hasDoublePleat = trouserTokens.includes('double_pleat') || trouserTokens.includes('pleated');
    const hasFlatFront   = trouserTokens.includes('flat_front');
    const hasCuffed      = trouserTokens.includes('cuffed');
    const hasSideTabs    = trouserTokens.includes('side_tabs');
    const beltToken      = trouserTokens.find(t => t.toLowerCase() === 'belt' || t.toLowerCase().startsWith('belt:'));
    const beltColour     = beltToken && beltToken.includes(':') ? beltToken.split(':', 2)[1].trim() : null;
    setTrPleats(
      hasSinglePleat ? 'single'
      : hasDoublePleat ? 'double'
      : hasFlatFront ? 'none'
      : null
    );
    setTrCuffs(!!hasCuffed);
    setTrWaist(
      hasSideTabs ? 'side_tabs'
      : beltToken ? 'belt'
      : null
    );
    setTrBeltColor(beltColour || null);
    setTrShoeType(applied.shoe_type || null);
    setTrShoeColor(applied.shoe_color || null);
    setTrouserRestructureOpen(false);
    // Iter 162 — reset dirty flag after hydration completes so the
    // NEXT tailor interaction with any restructure control marks the
    // state as dirty. `look` prop changes after every /edit-look
    // response so this fires on every mount + edit round-trip.
    setRestructureDirty(false);
  }, [look]);

  const handleRegenerate = () => {
    onRegenerate({
      ...look,
      customColor: suitColor,
      customShirt: SHIRT_COLORS.find(c => c.value === shirtColor)?.name || 'White',
      customShirtPattern: shirtPattern,
      customPant: PANT_COLORS.find(c => c.value === pantColor)?.name,
      customShoe: shoeType,
      customShoeColor: SHOE_COLORS.find(c => c.value === shoeColor)?.name || 'Black',
      customPattern: pattern,
      customLapel: lapel,
      customSatinLapel: isTuxedoLook ? satinLapel : undefined,
      customButtonCount: buttonCount,
      customButtonStyle: buttonStyle,
      customTie: tieType,
      customTieColor: tieType !== 'none' ? TIE_COLORS.find(c => c.value === tieColor)?.name : undefined,
      customTrouserStyle: trouserStyleCust,
      // Route through the image-edit endpoint — 3-5× faster, face
      // preserved. Dashboard.handleRegenerate falls back to a full regen
      // if the edit endpoint errors (e.g. look has no backend id yet).
      __quickEdit: true,
    });
  };

  // Instant-change handler for the Quick Tweaks panel. Applies a one-off
  // override (lapel or button-count) to the current look and fires
  // onRegenerate immediately — no "Apply" click required. The
  // `__quickEdit` flag routes the Dashboard handler to Gemini's image-edit
  // endpoint (surgical diff on the existing image, 3-5× faster, keeps face
  // pixel-identical) instead of a full regen. Backend cache_key makes
  // repeat tweaks free.
  const instantRegenerate = (overrides) => {
    if (isRegenerating) return;
    if (overrides.customLapel) setLapel(overrides.customLapel);
    if (typeof overrides.customSatinLapel === 'boolean') setSatinLapel(overrides.customSatinLapel);
    if (overrides.customButtonCount) setButtonCount(overrides.customButtonCount);
    if (overrides.customButtonStyle) setButtonStyle(overrides.customButtonStyle);
    onRegenerate({
      ...look,
      customColor: suitColor,
      customShirt: SHIRT_COLORS.find(c => c.value === shirtColor)?.name || 'White',
      customShirtPattern: shirtPattern,
      customPant: PANT_COLORS.find(c => c.value === pantColor)?.name,
      customShoe: shoeType,
      customShoeColor: SHOE_COLORS.find(c => c.value === shoeColor)?.name || 'Black',
      customPattern: pattern,
      customLapel: lapel,
      customSatinLapel: isTuxedoLook ? satinLapel : undefined,
      customButtonCount: buttonCount,
      customButtonStyle: buttonStyle,
      customTie: tieType,
      customTieColor: tieType !== 'none' ? TIE_COLORS.find(c => c.value === tieColor)?.name : undefined,
      customTrouserStyle: trouserStyleCust,
      ...overrides,
      __quickEdit: true,
    });
  };

  // ── Legibility-safe store accent colour ─────────────────────────
  // The modal background is near-black (#121215). If the store's primary
  // colour is too dark to read on it (luminance < 0.25), we lift it toward
  // white so the brand name remains legible without losing identity.
  const storeColor = React.useMemo(() => {
    const raw = (storeSettings?.primary_color || '#C2A878').trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(raw)) return '#C2A878';
    const r = parseInt(raw.slice(1, 3), 16) / 255;
    const g = parseInt(raw.slice(3, 5), 16) / 255;
    const b = parseInt(raw.slice(5, 7), 16) / 255;
    // Relative luminance (Rec.709 weighting — close enough for UI brand text)
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    if (lum >= 0.28) return raw;
    // Lift toward white until we hit a usable luminance, preserving hue
    const lift = Math.min(1, 0.28 / Math.max(lum, 0.05));
    const lr = Math.min(255, Math.round(r * 255 * lift + 60));
    const lg = Math.min(255, Math.round(g * 255 * lift + 60));
    const lb = Math.min(255, Math.round(b * 255 * lift + 60));
    return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`;
  }, [storeSettings?.primary_color]);
  const storeName = storeSettings?.store_name || '';

  if (!look) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#121215] border-[#27272A] text-[#F5F5F5] !max-w-3xl max-h-[90vh] overflow-y-auto z-[60]">
        <DialogHeader>
          {/* Header layout (Feb 2026 — final spec):
              • LOGO sits TOP-LEFT (absolute), brand mark anchor.
              • STORE NAME sits CENTRED on top, in store's primary colour
                (legibility-safe lift applied for very dark brands).
              • LOOK NAME sits CENTRED just under the store name, smaller +
                muted so the brand identity stays the loudest mark.
              • Logo and store-name+look-name are SEPARATE elements. */}
          <div className="relative min-h-[3.5rem] pt-1">
            {storeSettings?.store_logo && (
              <img
                src={storeSettings.store_logo}
                alt={storeName || 'Brand'}
                className="absolute left-0 top-0 h-7 w-auto max-w-[120px] object-contain"
                style={{ mixBlendMode: 'screen', filter: 'brightness(1.05) contrast(1.05)' }}
              />
            )}
            <div className="flex flex-col items-center text-center gap-1.5">
              {storeName && (
                <span
                  className="uppercase tracking-[0.28em] text-sm font-light leading-none"
                  style={{
                    color: storeColor,
                    fontFamily: 'Cormorant Garamond, serif',
                    letterSpacing: '0.28em',
                    textShadow: '0 1px 2px rgba(0,0,0,0.45)',
                  }}
                  data-testid="customize-modal-store-name"
                >
                  {storeName}
                </span>
              )}
              <DialogTitle
                className="text-[#D4D4D8]"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.125rem', fontWeight: 300, lineHeight: 1.2, letterSpacing: '0.01em' }}
              >
                {look.label}
              </DialogTitle>
            </div>
          </div>
          {/* Screen-reader-only description (a11y) — DialogDescription is
              required for Radix dialogs but visually hidden per spec. */}
          <DialogDescription className="sr-only">{look.label}</DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
          <div
            className="aspect-[3/4] bg-[#0A0A0C] border border-[#27272A] overflow-hidden relative group"
            style={{ cursor: look.image && !isRegenerating ? 'zoom-in' : 'default' }}
            ref={imgContainerRef}
            onMouseMove={(e) => {
              if (!look.image || isRegenerating) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const x = ((e.clientX - rect.left) / rect.width) * 100;
              const y = ((e.clientY - rect.top) / rect.height) * 100;
              const img = e.currentTarget.querySelector('img');
              if (img) {
                img.style.transformOrigin = `${x}% ${y}%`;
                img.style.transform = 'scale(2.25)';
              }
            }}
            onMouseLeave={(e) => {
              const img = e.currentTarget.querySelector('img');
              if (img) {
                img.style.transformOrigin = 'center center';
                img.style.transform = 'scale(1)';
              }
            }}
          >
            {/* Image stays mounted during regen — optimistic UI. Instead of
                wiping to a spinner (which feels slow), we keep the old image
                visible + apply a subtle blur + overlay a shimmer badge. The
                new image swaps in the instant Gemini returns. Result: the
                SAME 3-5s call feels ~2× faster because the user never looks
                at a blank screen. */}
            {look.image ? (
              <img
                src={(() => {
                  const v = activeImage;
                  if (!v || typeof v !== 'string') return '';
                  if (v.startsWith('data:') || v.startsWith('http') || v.startsWith('/api/')) return v;
                  return `data:image/jpeg;base64,${v}`;
                })()}
                alt={look.label}
                data-testid="customize-look-image"
                className={`w-full h-full object-contain transition-all duration-300 ease-out pointer-events-none select-none ${isRegenerating && variantIdx === 0 ? 'opacity-60 blur-[2px] scale-[0.99]' : ''}`}
                draggable={false}
                loading="eager"
                fetchPriority="high"
                decoding="sync"
              />
            ) : isRegenerating ? (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <Loader2 className="w-10 h-10 text-[hsl(var(--accent))] spinner" />
                <p className="text-sm text-[#A1A1AA] mt-3">Rendering…</p>
              </div>
            ) : null}
            {isRegenerating && look.image && variantIdx === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="bg-[#0A0A0C]/85 border border-[#C2A878]/40 px-4 py-2 flex items-center gap-2 backdrop-blur-sm" data-testid="customize-inline-loader">
                  <Loader2 className="w-3.5 h-3.5 text-[#C2A878] animate-spin" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#C2A878]">Applying edit</span>
                </div>
                {/* Shimmer sweep — pure CSS, no JS cost */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute inset-y-0 -left-full w-1/3 bg-gradient-to-r from-transparent via-[#C2A878]/10 to-transparent animate-[shimmer_1.4s_linear_infinite]" />
                </div>
              </div>
            )}
            {look.image && !isRegenerating && (
              <div className="absolute bottom-2 right-2 bg-black/60 text-white/80 text-[10px] uppercase tracking-wider px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                Hover to zoom
              </div>
            )}
          </div>
          {/* Iter 130k — Satin Lapel toggle REMOVED (Feb 2026).
              User request: this toggle sat under the photo when the
              card was a tuxedo and shipped confusion (tuxedos already
              default to satin lapels, and the equivalent conversion
              is available in the Silhouette Restructure section below
              via "Satin tuxedo lapel conversion"). Removing the
              duplicate control here to keep the card actions clean. */}
          {/* Feb 2026 — Customize Liner feature DISABLED at user request.
              The whole SuitDesignerModal (liner + designer) is reachable
              ONLY through this button, so hiding it dormant-fies the
              feature end-to-end without deleting any code. To re-enable,
              flip the guard back to:
                {look.image && !isRegenerating && onDesign && !isShirtLook && (...)}
              and remove the `false && ` prefix on the next line. */}
          {false && look.image && !isRegenerating && onDesign && !isShirtLook && (
            <button
              type="button"
              onClick={() => onDesign({ ...look, image: activeImage,
                linerSourcePath: variantIdx > 0 ? variants[variantIdx - 1]?.image_path : look.image_path })}
              className="w-full h-10 rounded-none uppercase tracking-widest text-[10px] font-semibold border border-[#C2A878] text-[#C2A878] hover:bg-[#C2A878]/10 transition-colors flex items-center justify-center gap-2"
              data-testid="customize-design-btn"
              title="Customize this look's liner"
            >
              <Palette className="w-3.5 h-3.5 opacity-70" />
              Customize Liner
            </button>
          )}
          {/* ── Variant carousel toolbar (moved below image Feb 2026) ──
              Originally these prev/next chevrons were absolutely
              positioned over the image, but the hover-zoom (scale 2.25×)
              made them virtually unusable — clicking near the edges
              triggered zoom rather than the button. Placing the toolbar
              directly underneath the image keeps it accessible at all
              times and clear of the zoom area.

              Feb 2026 iter 153 — toolbar STAYS visible while a new
              render is in flight so the consultant can toggle to
              "Prev" and show the client the previous look side-by-
              side with the badge saying "Applying edit" over the
              current image. Previously the toolbar disappeared the
              moment isRegenerating flipped true, which is exactly
              when the user WANTS to compare with what came before.
              We also mark the button visually with an accent border
              so it stops disappearing into the modal chrome. */}
          {variantCount > 1 && look.image && (
            <div
              className="flex items-center justify-between gap-2 px-1"
              data-testid="customize-carousel-toolbar"
            >
              <button
                type="button"
                onClick={() => setVariantIdx(i => (i - 1 + variantCount) % variantCount)}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#121215] hover:bg-[#1A1A1F] border border-[hsl(var(--accent))]/40 hover:border-[hsl(var(--accent))] text-[#F5F5F5] transition-colors"
                aria-label="Previous render"
                data-testid="customize-carousel-prev"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="text-[10px] uppercase tracking-widest">Prev</span>
              </button>
              <div
                className="flex items-center gap-2 text-[10px] uppercase tracking-widest"
                data-testid="customize-carousel-indicator"
              >
                <span className={variantIdx === 0 ? "text-[hsl(var(--accent))]" : "text-[#A1A1AA]"}>
                  {activeLabel}
                </span>
                <span className="text-[#52525B]">·</span>
                <span className="text-[#A1A1AA]">{variantIdx + 1} / {variantCount}</span>
              </div>
              <button
                type="button"
                onClick={() => setVariantIdx(i => (i + 1) % variantCount)}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#121215] hover:bg-[#1A1A1F] border border-[hsl(var(--accent))]/40 hover:border-[hsl(var(--accent))] text-[#F5F5F5] transition-colors"
                aria-label="Next render"
                data-testid="customize-carousel-next"
              >
                <span className="text-[10px] uppercase tracking-widest">Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          {/* ── Pant recolour controls (Gemini edit-look) ─────────────
              Gated to Business Casual sports-coat / blazer looks only
              per user spec — those are the only renders where a
              pant-only swap reads cleanly because the jacket is a
              separate (odd) garment from the trousers. Collapsible so
              it doesn't dominate the modal on every open. */}
          {look.image && _showPantRecolour && (
            <div data-testid="pant-recolor-row">
              <button
                type="button"
                onClick={() => setRecolourOpen(o => !o)}
                className="w-full flex items-center justify-between text-left group hover:bg-[#121215] -mx-1 px-1 py-1 transition-colors"
                data-testid="pant-recolor-toggle"
              >
                <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA] group-hover:text-[#F5F5F5]">
                  Recolour · Pants
                </span>
                <span className="flex items-center gap-2">
                  {isRegenerating && (
                    <span className="text-[10px] uppercase tracking-widest text-[#52525B]">Rendering…</span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-[#52525B] transition-transform ${recolourOpen ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {recolourOpen && (
                <div className="mt-2">
                  <div className="grid grid-cols-6 gap-1.5">
                    {PANT_COLORS.map(c => {
                      const active = pendingPantName === c.name;
                      return (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => {
                            if (isRegenerating) return;
                            setPendingPantName(c.name);
                            setPantColor(c.value);
                            // Iter 156 — the pant chip now bundles
                            // any pending trouser cut / belt colour /
                            // shoe style / shoe colour picks that
                            // haven't been Applied yet. That way
                            // "swap pant colour" carries the
                            // restructure attributes along in ONE
                            // edit rather than requiring two calls
                            // (which used to lose the second edit).
                            // Iter 162 — GATE the bundle path on the
                            // `restructureDirty` flag, not on "any
                            // restructure state is populated". After
                            // iter 160 hydrates the six slots from
                            // applied_edits, the old check was always
                            // true after any prior restructure. That
                            // pushed every subsequent pant chip click
                            // through the structural bundle path
                            // (weaker jacket preservation) instead of
                            // the dedicated pant_only_lock, causing
                            // the "jackets are changing pant colour"
                            // drift the tailor just reported.
                            //
                            // Only bundle when the tailor has ACTIVELY
                            // touched a restructure control since the
                            // last edit. Prior applied_edits stay
                            // visible via the backend's `image_path`
                            // source anchor for single-attribute
                            // deltas.
                            const hasRestructurePending = restructureDirty;
                            if (hasRestructurePending) {
                              const tokens = [];
                              if (trPleats === 'single') tokens.push('single_pleat');
                              else if (trPleats === 'double') tokens.push('double_pleat');
                              else if (trPleats === 'none') tokens.push('flat_front');
                              if (trCuffs) tokens.push('cuffed');
                              if (trWaist === 'side_tabs') {
                                tokens.push('side_tabs');
                              } else if (trWaist === 'belt') {
                                tokens.push(trBeltColor ? `belt:${trBeltColor}` : 'belt');
                              } else if (trBeltColor) {
                                tokens.push(`belt:${trBeltColor}`);
                              }
                              const composite = tokens.join('+');
                              const payload = {
                                customPant: c.name,
                                __pantWithRestructure: true,
                              };
                              if (composite)   payload.customTrouserStyle = composite;
                              if (trShoeType)  payload.customShoeType   = trShoeType;
                              if (trShoeColor) payload.customShoeColor  = trShoeColor;
                              instantRegenerate(payload);
                              // Iter 157 — reset the restructure
                              // selections AFTER bundling so the
                              // NEXT pant-colour chip click is a
                              // pure colour swap. Otherwise the
                              // tailor changes colour again and the
                              // restructure re-applies on top of
                              // the already-restructured render,
                              // which reads as "the pant colour
                              // changed everything".
                              setTrPleats(null);
                              setTrCuffs(false);
                              setTrWaist(null);
                              setTrBeltColor(null);
                              setTrShoeType(null);
                              setTrShoeColor(null);
                              setRestructureDirty(false);
                            } else {
                              // Pure colour-only edit — dedicated
                              // pant_only_lock (jacket-crop anchor +
                              // deterministic pixel restore).
                              instantRegenerate({ customPant: c.name, __pantOnly: true });
                            }
                          }}
                          disabled={isRegenerating}
                          title={c.name}
                          className={`aspect-square border transition-all ${active ? 'border-[hsl(var(--accent))] scale-105' : 'border-[#27272A] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                          style={{ backgroundColor: c.value }}
                          data-testid={`pant-recolor-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                      );
                    })}
                  </div>
                  {pendingPantName && (
                    <p className="text-[10px] text-[#52525B] mt-1.5" data-testid="pant-recolor-active-label">
                      Re-rendering with {pendingPantName} pants — jacket and shoes locked.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {/* ── Feb 2026 · Restructure · Trousers (multi-select) ────────
              Mirrors the Vest section inside the Silhouette panel —
              pleats + cuffs + side tabs can all be picked together
              and applied in ONE surgical edit. Gated to business
              casual + custom-fabric sport-coats / blazers per user
              spec. The pill-instant behaviour was retired here per
              user feedback: instant chips fired one edit per click,
              stacking drift; the Apply button pattern fires exactly
              one composite edit so pose / shoe / face never wobble. */}
          {look.image && _showTrouserCut && (
            <div data-testid="trouser-restructure-row">
              <button
                type="button"
                onClick={() => setTrouserRestructureOpen(o => !o)}
                className="w-full flex items-center justify-between text-left group hover:bg-[#121215] -mx-1 px-1 py-1 transition-colors"
                data-testid="trouser-restructure-toggle"
              >
                <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA] group-hover:text-[#F5F5F5]">
                  Restructure · Trousers &amp; Shoes
                </span>
                <span className="flex items-center gap-2">
                  {isRegenerating && (
                    <span className="text-[10px] uppercase tracking-widest text-[#52525B]">Rendering…</span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-[#52525B] transition-transform ${trouserRestructureOpen ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {trouserRestructureOpen && (
                <div className="mt-2 space-y-3" data-testid="trouser-restructure-panel">
                  {/* Pleats radio — single-select. `none` is the
                      default no-op. */}
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Pleats</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { value: 'none',   label: 'No pleats',    hint: 'Flat front' },
                        { value: 'single', label: 'Single pleat', hint: 'One per side' },
                        { value: 'double', label: 'Double pleat', hint: 'Two per side' },
                      ].map(opt => {
                        const active = trPleats === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              if (isRegenerating) return;
                              setTrPleats(active ? null : opt.value);
                              setRestructureDirty(true);
                            }}
                            disabled={isRegenerating}
                            className={`px-2 py-1.5 border text-left transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                            data-testid={`trouser-restructure-pleats-${opt.value}`}
                          >
                            <span className="block text-[10px] uppercase tracking-wider leading-tight">{opt.label}</span>
                            <span className="block text-[9px] text-[#52525B] mt-0.5 leading-tight normal-case">{opt.hint}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Cuffs toggle — independent checkbox. */}
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Cuffs</p>
                    <button
                      type="button"
                      onClick={() => {
                        if (isRegenerating) return;
                        setTrCuffs(v => !v);
                        setRestructureDirty(true);
                      }}
                      disabled={isRegenerating}
                      className={`w-full px-3 py-2 border text-left transition-all ${trCuffs ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                      data-testid="trouser-restructure-cuffs"
                      aria-pressed={trCuffs}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-3.5 h-3.5 border ${trCuffs ? 'bg-[hsl(var(--accent))] border-[hsl(var(--accent))]' : 'border-[#52525B]'} flex items-center justify-center flex-shrink-0`}>
                          {trCuffs && (
                            <svg className="w-2.5 h-2.5 text-[#0A0A0C]" viewBox="0 0 12 12" fill="none">
                              <path d="M2.5 6.5L5 9L10 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1">
                          <span className="block text-[10px] uppercase tracking-wider">Cuffed hem</span>
                          <span className="block text-[9px] text-[#52525B] mt-0.5 normal-case">Turned-up cuffs at the ankle</span>
                        </div>
                      </div>
                    </button>
                  </div>
                  {/* Waistband — mutually exclusive: Belt (with belt
                      loops + leather belt) vs Side tabs (adjusters
                      that REPLACE belt loops — no belt loops, no
                      belt). Neither selected = leave the default,
                      which the backend now renders as belt loops +
                      shoe-matched belt by default. */}
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Waistband</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {/* Belt option — adds belt loops + leather belt.
                          Selecting turns Side tabs OFF. */}
                      <button
                        type="button"
                        onClick={() => {
                          if (isRegenerating) return;
                          const next = trWaist === 'belt' ? null : 'belt';
                          setTrWaist(next);
                          // Clear belt colour when leaving belt mode.
                          if (next !== 'belt') setTrBeltColor(null);
                          setRestructureDirty(true);
                        }}
                        disabled={isRegenerating}
                        className={`px-2 py-2 border text-left transition-all ${trWaist === 'belt' ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                        data-testid="trouser-restructure-belt"
                        aria-pressed={trWaist === 'belt'}
                      >
                        <span className="block text-[10px] uppercase tracking-wider">Belt</span>
                        <span className="block text-[9px] text-[#52525B] mt-0.5 normal-case">Belt loops + leather belt</span>
                      </button>
                      {/* Side tabs option — adjusters that REPLACE
                          belt loops. Selecting turns Belt OFF and
                          clears belt colour. */}
                      <button
                        type="button"
                        onClick={() => {
                          if (isRegenerating) return;
                          const next = trWaist === 'side_tabs' ? null : 'side_tabs';
                          setTrWaist(next);
                          if (next === 'side_tabs') setTrBeltColor(null);
                          setRestructureDirty(true);
                        }}
                        disabled={isRegenerating}
                        className={`px-2 py-2 border text-left transition-all ${trWaist === 'side_tabs' ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                        data-testid="trouser-restructure-side-tabs"
                        aria-pressed={trWaist === 'side_tabs'}
                      >
                        <span className="block text-[10px] uppercase tracking-wider">Side tabs</span>
                        <span className="block text-[9px] text-[#52525B] mt-0.5 normal-case">Adjusters, no belt loops</span>
                      </button>
                    </div>
                    {/* Belt colour picker — only surfaces when the
                        Belt option is active. Uses the shared shoe
                        palette (belts should match shoes by classic
                        menswear rule) plus a couple of extra dress
                        colours for stylistic pairings. */}
                    {trWaist === 'belt' && (
                      <div className="mt-2" data-testid="trouser-restructure-belt-colour-picker">
                        <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">
                          Belt colour
                          <span className="text-[9px] text-[#52525B] normal-case ml-2">{trBeltColor ? trBeltColor : '— matches shoes by default'}</span>
                        </p>
                        <div className="grid grid-cols-6 gap-1.5">
                          {[
                            { name: 'Black',        hex: '#0A0A0C' },
                            { name: 'Dark Brown',   hex: '#3A2418' },
                            { name: 'Chocolate',    hex: '#503020' },
                            { name: 'Cognac',       hex: '#9B5E2E' },
                            { name: 'Tan',          hex: '#C19A6B' },
                            { name: 'Oxblood',      hex: '#582430' },
                          ].map(c => {
                            const active = trBeltColor === c.name;
                            return (
                              <button
                                key={c.name}
                                type="button"
                                onClick={() => {
                                  if (isRegenerating) return;
                                  const nextBelt = active ? null : c.name;
                                  setTrBeltColor(nextBelt);
                                  // Iter 154 — belt & shoes always
                                  // share the same leather colour
                                  // family (classic menswear rule).
                                  // Auto-sync the shoe colour when
                                  // the belt colour changes UNLESS
                                  // the tailor has explicitly picked
                                  // a different shoe colour already
                                  // (i.e. they want a mismatch).
                                  if (!trShoeColor || trShoeColor === trBeltColor) {
                                    setTrShoeColor(nextBelt);
                                  }
                                  setRestructureDirty(true);
                                }}
                                disabled={isRegenerating}
                                title={c.name}
                                className={`aspect-square border transition-all ${active ? 'border-[hsl(var(--accent))] scale-105' : 'border-[#27272A] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                                style={{ backgroundColor: c.hex }}
                                data-testid={`trouser-restructure-belt-colour-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                              />
                            );
                          })}
                        </div>
                        <p className="text-[9px] text-[#52525B] mt-1 normal-case">
                          Belt &amp; shoes share the same leather colour by default. Override shoe colour below to break the pairing.
                        </p>
                      </div>
                    )}
                  </div>
                  {/* ── Iter 154 · Shoes ────────────────────────────
                      Shoe restructure surfaced INSIDE the Trousers
                      & Shoes panel per user spec. Style picker
                      (Oxford / Derby / Loafer / Monk / Chelsea /
                      Sneaker) + colour picker. Selecting a belt
                      colour above auto-syncs the shoe colour, so
                      the tailor typically doesn't need to touch
                      the shoe colour separately. */}
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Shoes · style</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { value: 'oxford',       label: 'Oxford',   hint: 'Closed-lace dress' },
                        { value: 'derby',        label: 'Derby',    hint: 'Open-lace dress' },
                        { value: 'loafer',       label: 'Loafer',   hint: 'Slip-on, no laces' },
                        { value: 'monk_strap',   label: 'Monk',     hint: 'Buckle strap' },
                        { value: 'chelsea_boot', label: 'Chelsea',  hint: 'Ankle boot, elastic' },
                        { value: 'sneaker',      label: 'Sneaker',  hint: 'Clean white low-top' },
                      ].map(opt => {
                        const active = trShoeType === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              if (isRegenerating) return;
                              setTrShoeType(active ? null : opt.value);
                              // Sneakers stay white regardless of
                              // belt colour — clear any shoe colour
                              // override when the tailor picks
                              // sneakers so the render doesn't fight
                              // the classic clean-white finish.
                              if (!active && opt.value === 'sneaker') setTrShoeColor(null);
                              setRestructureDirty(true);
                            }}
                            disabled={isRegenerating}
                            title={opt.hint}
                            className={`px-2 py-1.5 border text-left transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                            data-testid={`trouser-restructure-shoe-style-${opt.value}`}
                          >
                            <span className="block text-[10px] uppercase tracking-wider leading-tight">{opt.label}</span>
                            <span className="block text-[9px] text-[#52525B] mt-0.5 leading-tight normal-case">{opt.hint}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Shoe colour — six heritage leather chips, same
                      palette as the belt picker so the linkage reads
                      cleanly. Hidden when Sneakers is selected. */}
                  {trShoeType !== 'sneaker' && (
                    <div data-testid="trouser-restructure-shoe-colour-picker">
                      <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">
                        Shoes · colour
                        <span className="text-[9px] text-[#52525B] normal-case ml-2">
                          {trShoeColor ? trShoeColor : (trBeltColor ? `matching ${trBeltColor} belt` : '— unchanged')}
                        </span>
                      </p>
                      <div className="grid grid-cols-6 gap-1.5">
                        {[
                          { name: 'Black',        hex: '#0A0A0C' },
                          { name: 'Dark Brown',   hex: '#3A2418' },
                          { name: 'Chocolate',    hex: '#503020' },
                          { name: 'Cognac',       hex: '#9B5E2E' },
                          { name: 'Tan',          hex: '#C19A6B' },
                          { name: 'Oxblood',      hex: '#582430' },
                        ].map(c => {
                          const active = trShoeColor === c.name;
                          return (
                            <button
                              key={c.name}
                              type="button"
                              onClick={() => {
                                if (isRegenerating) return;
                                setTrShoeColor(active ? null : c.name);
                                setRestructureDirty(true);
                              }}
                              disabled={isRegenerating}
                              title={c.name}
                              className={`aspect-square border transition-all ${active ? 'border-[hsl(var(--accent))] scale-105' : 'border-[#27272A] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                              style={{ backgroundColor: c.hex }}
                              data-testid={`trouser-restructure-shoe-colour-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Apply — greyed out until at least one option
                      differs from the default no-op state. Fires a
                      SINGLE composite edit so the structural lock
                      sees one call, not three. */}
                  <button
                    type="button"
                    disabled={
                      isRegenerating
                      || (!trPleats && !trCuffs && !trWaist && !trBeltColor && !trShoeType && !trShoeColor)
                      // Iter 162 — also disable when the current state
                      // has NOT been touched by the tailor since the
                      // last hydration. Without this, re-clicking
                      // Apply on a look whose applied_edits already
                      // match the panel state fires a bundled edit
                      // that produces an identical merged payload,
                      // cache-hits, and shows the misleading "Applied
                      // instantly (cached)" toast with no visual
                      // change. The no-op guard in Dashboard would
                      // also catch this, but disabling the button is
                      // the correct upstream signal.
                      || !restructureDirty
                    }
                    onClick={() => {
                      const tokens = [];
                      if (trPleats === 'single') tokens.push('single_pleat');
                      else if (trPleats === 'double') tokens.push('double_pleat');
                      else if (trPleats === 'none') tokens.push('flat_front');
                      if (trCuffs) tokens.push('cuffed');
                      if (trWaist === 'side_tabs') {
                        tokens.push('side_tabs');
                      } else if (trWaist === 'belt') {
                        tokens.push(trBeltColor ? `belt:${trBeltColor}` : 'belt');
                      } else if (trBeltColor) {
                        // Belt colour picked without explicit belt
                        // toggle → still emit a belt token so the
                        // backend renders the requested colour.
                        tokens.push(`belt:${trBeltColor}`);
                      }
                      const composite = tokens.join('+');
                      // Iter 154 — shoe fields ride alongside the
                      // trouser composite via the same
                      // __trouserCutOnly bundle. Backend structural
                      // lock accepts both together.
                      const payload = { __trouserCutOnly: true };
                      if (composite) payload.customTrouserStyle = composite;
                      if (trShoeType)  payload.customShoeType  = trShoeType;
                      if (trShoeColor) payload.customShoeColor = trShoeColor;
                      // If nothing to send, bail (button should be
                      // disabled already but defense in depth).
                      if (!payload.customTrouserStyle && !payload.customShoeType && !payload.customShoeColor) return;
                      setPendingTrouserCut(composite || (payload.customShoeType || payload.customShoeColor || null));
                      instantRegenerate(payload);
                      // Iter 157 — reset the restructure selections
                      // AFTER firing so a subsequent pant-colour
                      // chip click doesn't re-bundle the same
                      // attributes on top of the new base image
                      // (which already carries them). Without this
                      // reset the user would re-apply pleats + belt +
                      // shoe every time they tried to only change
                      // the trouser colour, which reads as "the pant
                      // colour changed everything" to the tailor.
                      setTrPleats(null);
                      setTrCuffs(false);
                      setTrWaist(null);
                      setTrBeltColor(null);
                      setTrShoeType(null);
                      setTrShoeColor(null);
                      setRestructureDirty(false);
                    }}
                    className="w-full px-3 py-2 border-2 text-[10px] uppercase tracking-widest transition-all border-[hsl(var(--accent))] bg-[hsl(var(--accent))] text-[#0A0A0C] font-semibold hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-[#52525B] disabled:border-[#27272A]"
                    data-testid="trouser-restructure-apply"
                  >
                    {isRegenerating ? 'Re-rendering…' : 'Apply trouser & shoe edit'}
                  </button>
                  <p className="text-[10px] text-[#52525B]">
                    Jacket, shirt, tie, face and pose stay locked. Only the selected trouser &amp; shoe attributes change.
                  </p>
                </div>
              )}
            </div>
          )}
          {/* ── Sep 2026 (user request) · Restructure · Coat length ──
              Instant-apply pill pair on overcoat cards. Tapping
              "Mid-calf" fires a `__coatLengthOnly` edit — Gemini
              redraws only the hem, everything else pixel-locked. */}
          {look.image && _showCoatLengthEditor && (
            <div data-testid="coat-length-edit-row">
              <button
                type="button"
                onClick={() => setCoatLengthOpen(o => !o)}
                className="w-full flex items-center justify-between text-left group hover:bg-[#121215] -mx-1 px-1 py-1 transition-colors"
                data-testid="coat-length-edit-toggle"
              >
                <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA] group-hover:text-[#F5F5F5]">
                  Restructure · Coat length
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-[#52525B] transition-transform ${coatLengthOpen ? 'rotate-180' : ''}`} />
              </button>
              {coatLengthOpen && (
                <div className="mt-2">
                  <div className="grid grid-cols-3 gap-1.5">
                    {[
                      { value: 'knee',        label: 'Knee',        hint: 'Standard' },
                      { value: 'mid_calf',    label: 'Mid-calf',    hint: 'Extended' },
                      { value: 'bottom_calf', label: 'Bottom calf', hint: 'Full-length' },
                    ].map(opt => {
                      const active = pendingCoatLength === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            if (isRegenerating) return;
                            setPendingCoatLength(opt.value);
                            instantRegenerate({ customCoatLength: opt.value, __coatLengthOnly: true });
                          }}
                          disabled={isRegenerating}
                          className={`px-2 py-1.5 border text-left transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                          data-testid={`coat-length-edit-${opt.value}`}
                        >
                          <div className="text-[10px] uppercase tracking-wider">{opt.label}</div>
                          <div className="text-[8px] uppercase tracking-widest text-[#52525B] mt-0.5">{opt.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                  {pendingCoatLength && (
                    <p className="text-[10px] text-[#52525B] mt-1.5" data-testid="coat-length-edit-active-label">
                      Re-rendering coat with {
                        pendingCoatLength === 'mid_calf' ? 'mid-calf'
                        : pendingCoatLength === 'bottom_calf' ? 'full-length (bottom-of-calf)'
                        : 'knee-length'
                      } hem — fabric, buttons, pose and face locked.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {/* ── Dress-shirt Collar editor (Feb 2026) ──────────────────
              Surfaced ONLY for dress-shirt looks. Fires a surgical
              edit with `collar_style` set. Casual shirts hide this
              row per user spec (pants edit is their only option). */}
          {look.image && isDressShirtLook && (
            <div data-testid="collar-edit-row">
              <button
                type="button"
                onClick={() => setCollarOpen(o => !o)}
                className="w-full flex items-center justify-between text-left group hover:bg-[#121215] -mx-1 px-1 py-1 transition-colors"
                data-testid="collar-edit-toggle"
              >
                <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA] group-hover:text-[#F5F5F5]">
                  Restructure · Collar
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-[#52525B] transition-transform ${collarOpen ? 'rotate-180' : ''}`} />
              </button>
              {collarOpen && (
                <div className="mt-2">
                  <div className="grid grid-cols-2 gap-1.5">
                    {SHIRT_COLLAR_OPTIONS.map(opt => {
                      const active = pendingCollarName === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            if (isRegenerating) return;
                            setPendingCollarName(opt.value);
                            instantRegenerate({ customCollar: opt.value, __collarOnly: true });
                          }}
                          disabled={isRegenerating}
                          className={`px-2 py-1.5 border text-[10px] uppercase tracking-wider transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                          data-testid={`collar-edit-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {pendingCollarName && (
                    <p className="text-[10px] text-[#52525B] mt-1.5" data-testid="collar-edit-active-label">
                      Re-rendering shirt with new collar — shirt fabric, cuffs and pants locked.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {/* ── Dress-shirt Cuff editor (Feb 2026) ────────────────────
              Same mechanic as the collar row above. Casual shirts
              hide this row per user spec. */}
          {look.image && isDressShirtLook && (
            <div data-testid="cuff-edit-row">
              <button
                type="button"
                onClick={() => setCuffOpen(o => !o)}
                className="w-full flex items-center justify-between text-left group hover:bg-[#121215] -mx-1 px-1 py-1 transition-colors"
                data-testid="cuff-edit-toggle"
              >
                <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA] group-hover:text-[#F5F5F5]">
                  Restructure · Cuff
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-[#52525B] transition-transform ${cuffOpen ? 'rotate-180' : ''}`} />
              </button>
              {cuffOpen && (
                <div className="mt-2">
                  <div className="grid grid-cols-2 gap-1.5">
                    {SHIRT_CUFF_OPTIONS.map(opt => {
                      const active = pendingCuffName === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            if (isRegenerating) return;
                            setPendingCuffName(opt.value);
                            instantRegenerate({ customCuff: opt.value, __cuffOnly: true });
                          }}
                          disabled={isRegenerating}
                          className={`px-2 py-1.5 border text-[10px] uppercase tracking-wider transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                          data-testid={`cuff-edit-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {pendingCuffName && (
                    <p className="text-[10px] text-[#52525B] mt-1.5" data-testid="cuff-edit-active-label">
                      Re-rendering shirt with new cuff — shirt fabric, collar and pants locked.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {/* ── Button recolour (Gemini edit-look) ────────────────────
              Same mechanism as the pant chips above but tuned to the
              button hardware — round 3D-button swatches (radial-
              gradient sheen + inner shadow) instead of flat squares.
              Sends `__buttonsOnly: true` so Dashboard.handleRegenerate
              strips every other field from the payload, letting the
              backend's `is_buttons_only_edit` lock fire correctly. */}
          {look.image && _showButtonRecolour && (
            <div data-testid="button-recolor-row">
              <button
                type="button"
                onClick={() => setButtonsOpen(o => !o)}
                className="w-full flex items-center justify-between text-left group hover:bg-[#121215] -mx-1 px-1 py-1 transition-colors"
                data-testid="button-recolor-toggle"
              >
                <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA] group-hover:text-[#F5F5F5]">
                  Recolour · Buttons
                </span>
                <span className="flex items-center gap-2">
                  {isRegenerating && pendingButtonName && (
                    <span className="text-[10px] uppercase tracking-widest text-[#52525B]">Rendering…</span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-[#52525B] transition-transform ${buttonsOpen ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {buttonsOpen && (
                <div className="mt-2">
                  <div className="grid grid-cols-6 gap-1.5">
                    {BUTTON_COLORS.map(c => {
                      const active = pendingButtonName === c.name;
                      // Holes look better dark for light buttons (horn,
                      // pearl, white, brass, gold) and slightly lighter
                      // for already-dark buttons (gunmetal, navy) so
                      // they read as actual depressions instead of
                      // disappearing into the body.
                      const holeFill = c.name === 'Gunmetal' || c.name === 'Black Horn'
                        ? 'rgba(255,255,255,0.18)'
                        : 'rgba(0,0,0,0.55)';
                      return (
                        <button
                          key={c.name}
                          type="button"
                          onClick={() => {
                            if (isRegenerating) return;
                            setPendingButtonName(c.name);
                            instantRegenerate({ customButtonColor: c.name, __buttonsOnly: true });
                          }}
                          disabled={isRegenerating}
                          title={c.name}
                          className={`aspect-square rounded-full border-2 transition-all relative ${active ? 'border-[hsl(var(--accent))] scale-110' : 'border-[#27272A] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                          style={{
                            background: `radial-gradient(circle at 32% 30%, ${c.hi} 0%, ${c.base} 55%, ${c.base} 100%)`,
                            boxShadow: `inset -3px -4px 6px rgba(0,0,0,0.45), inset 2px 3px 4px rgba(255,255,255,0.18)`,
                          }}
                          data-testid={`button-recolor-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {/* Material texture overlay — horn grain /
                              brushed metal / iridescent pearl / smooth
                              enamel. Driven by the `texture` field on
                              each BUTTON_COLORS entry; CSS classes live
                              in index.css. Gives each chip a distinct
                              material reading instead of all 12 looking
                              like the same plastic disc. */}
                          {c.texture && (
                            <span
                              className={`absolute inset-0 rounded-full pointer-events-none button-texture button-texture-${c.texture}`}
                              aria-hidden="true"
                            />
                          )}
                          {/* Inner concentric rim — every real tailoring
                              button has this raised inner ring. A
                              one-pixel darker inset ring inside ~14% of
                              the edge fakes it cleanly. */}
                          <span
                            className="absolute inset-[14%] rounded-full pointer-events-none"
                            style={{
                              boxShadow: `inset 0 0 0 1px rgba(0,0,0,0.35), inset 0 1px 2px rgba(0,0,0,0.25), inset 0 -1px 2px rgba(255,255,255,0.12)`,
                            }}
                          />
                          {/* 4-hole stitching pattern — 2×2 grid at the
                              centre of the button, with each hole drawn
                              as a tiny inset circle so it reads as a
                              real depression. Universal across every
                              button material for visual consistency
                              (real horn / pearl / metal buttons all use
                              4-hole or 2-hole sews — 4-hole is the
                              tailoring default). */}
                          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className="grid grid-cols-2 grid-rows-2 gap-[16%] w-[34%] h-[34%]">
                              {[0, 1, 2, 3].map(i => (
                                <span
                                  key={i}
                                  className="rounded-full"
                                  style={{
                                    backgroundColor: holeFill,
                                    boxShadow: `inset 0 1px 1px rgba(0,0,0,0.6), 0 0.5px 0 rgba(255,255,255,0.12)`,
                                  }}
                                />
                              ))}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {pendingButtonName && (
                    <p className="text-[10px] text-[#52525B] mt-1.5" data-testid="button-recolor-active-label">
                      Re-rendering with {pendingButtonName} buttons — jacket fabric and pants locked.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {/* ── Tie recolour (Iter 88 — full suits / wedding with ties) ──
              Same edit-look mechanism as buttons + pants. Sends
              `__tieOnly: true` so Dashboard.handleRegenerate strips the
              payload, letting the backend lock jacket fabric, trousers,
              shoes, and face — only the tie's colour changes. */}
          {_showTieRecolour && (
            <div data-testid="tie-recolor-row">
              <button
                type="button"
                onClick={() => setTieOpen(o => !o)}
                className="w-full flex items-center justify-between text-left group hover:bg-[#121215] -mx-1 px-1 py-1 transition-colors"
                data-testid="tie-recolor-toggle"
              >
                <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA] group-hover:text-[#F5F5F5]">
                  Recolour · Tie
                </span>
                <span className="flex items-center gap-2">
                  {isRegenerating && pendingTieName && (
                    <span className="text-[10px] uppercase tracking-widest text-[#52525B]">Rendering…</span>
                  )}
                  <ChevronDown className={`w-3.5 h-3.5 text-[#52525B] transition-transform ${tieOpen ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {tieOpen && (
                <div className="mt-2">
                  {/* Iter 114 — "No Tie" pill sits above the colour grid.
                      Clicking it fires a strict no-tie-only surgical edit
                      that removes the necktie / bow tie while locking
                      jacket, shirt, pants, shoes and face pixel-identical.
                      Backend detects `edits == {tie_type: 'none'}` and
                      injects the NO-TIE EDIT lock at the top of the
                      Gemini prompt. */}
                  <button
                    type="button"
                    onClick={() => {
                      if (isRegenerating) return;
                      setPendingTieName('No Tie');
                      instantRegenerate({ customTie: 'none', __noTieOnly: true });
                    }}
                    disabled={isRegenerating}
                    className={`w-full mb-2 px-3 py-2 border-2 text-left flex items-center gap-2 transition-all ${
                      pendingTieName === 'No Tie'
                        ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                        : 'border-[#27272A] hover:border-[#52525B]'
                    } ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                    data-testid="tie-recolor-no-tie"
                  >
                    <span className="text-[10px] uppercase tracking-widest text-[#F5F5F5] font-medium">No Tie</span>
                    <span className="text-[10px] uppercase tracking-wider text-[#52525B]">· open collar</span>
                  </button>
                  <div className="grid grid-cols-6 gap-1.5">
                    {TIE_COLORS.map(c => {
                      const active = pendingTieName === c.name;
                      return (
                        <button
                          key={c.name}
                          type="button"
                          onClick={() => {
                            if (isRegenerating) return;
                            setPendingTieName(c.name);
                            instantRegenerate({ customTieColor: c.name, __tieOnly: true });
                          }}
                          disabled={isRegenerating}
                          title={c.name}
                          className={`aspect-square border-2 transition-all ${active ? 'border-[hsl(var(--accent))] scale-110' : 'border-[#27272A] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                          style={{
                            // Silk-tie sheen — diagonal gradient mimics
                            // the natural light catch on a knotted four-
                            // in-hand. Same visual idea as the pant
                            // chips (flat squares) but with a soft
                            // highlight band so each colour reads as
                            // "silk", not "paint".
                            background: `linear-gradient(135deg, ${c.value} 0%, ${c.value} 40%, rgba(255,255,255,0.12) 50%, ${c.value} 60%, ${c.value} 100%)`,
                          }}
                          data-testid={`tie-recolor-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                      );
                    })}
                  </div>
                  {pendingTieName && (
                    <p className="text-[10px] text-[#52525B] mt-1.5" data-testid="tie-recolor-active-label">
                      {pendingTieName === 'No Tie'
                        ? 'Removing tie — opening the collar. Jacket, pants, shoes, and face locked.'
                        : `Re-rendering with ${pendingTieName} tie — jacket, pants, shoes, and face locked.`}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {/* ── Silhouette dropdown (Iter 115) ──
              Multi-choice restructure of an existing render:
              • SB ↔ DB conversion
              • Open / Close jacket
              • Add / Remove vest
              Multiple choices can be stacked and applied in one surgical
              pass. Fires `__silhouetteOnly: true` so Dashboard sends only
              the silhouette fields and the backend's silhouette_only_lock
              engages (keeps fabric, tie, face, pose pixel-identical). */}
          {_showSilhouetteEditor && (
            <div data-testid="silhouette-row">
              <button
                type="button"
                onClick={() => setSilhouetteOpen(o => !o)}
                className="w-full flex items-center justify-between text-left group hover:bg-[#121215] -mx-1 px-1 py-1 transition-colors"
                data-testid="silhouette-toggle"
              >
                <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA] group-hover:text-[#F5F5F5]">
                  Restructure · Silhouette
                </span>
                <span className={`text-[10px] text-[#52525B] transition-transform ${silhouetteOpen ? 'rotate-180' : ''}`}>▾</span>
              </button>
              {silhouetteOpen && (
                <div className="mt-2 space-y-3" data-testid="silhouette-panel">
                  {/* SB ↔ DB — the marquee edit. Toggle chips: pick one
                      direction or "no change". */}
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Jacket style</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { value: 'single_breasted', label: 'Single-breasted' },
                        { value: 'double_breasted', label: 'Double-breasted' },
                      ].map(opt => {
                        const active = silhJacketStyle === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              if (isRegenerating) return;
                              setSilhJacketStyle(active ? null : opt.value);
                            }}
                            disabled={isRegenerating}
                            className={`px-2 py-1.5 border text-[10px] uppercase tracking-wider transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                            data-testid={`silhouette-jacket-style-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Open ↔ Closed jacket */}
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Jacket state</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { value: 'open', label: 'Open' },
                        { value: 'closed', label: 'Closed' },
                      ].map(opt => {
                        const active = silhJacketState === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              if (isRegenerating) return;
                              setSilhJacketState(active ? null : opt.value);
                            }}
                            disabled={isRegenerating}
                            className={`px-2 py-1.5 border text-[10px] uppercase tracking-wider transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                            data-testid={`silhouette-jacket-state-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Vest add / remove */}
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Vest / waistcoat</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { value: 'with', label: 'Add vest' },
                        { value: 'without', label: 'Remove vest' },
                      ].map(opt => {
                        const active = silhVest === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              if (isRegenerating) return;
                              const next = active ? null : opt.value;
                              setSilhVest(next);
                              // Reset the vest-style detail selectors
                              // whenever the user leaves Add-vest mode
                              // (so a stale style can't ship with a
                              // Remove-vest edit).
                              if (next !== 'with') {
                                setSilhVestStyle(null);
                                setSilhVestButtons(null);
                                setSilhVestLapel(null);
                              } else {
                                // Sensible defaults when entering
                                // Add-vest mode.
                                setSilhVestStyle('single_breasted');
                                setSilhVestButtons('5');
                                setSilhVestLapel('none');
                              }
                            }}
                            disabled={isRegenerating}
                            className={`px-2 py-1.5 border text-[10px] uppercase tracking-wider transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                            data-testid={`silhouette-vest-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    {/* Feb 2026 — Vest style + button + lapel selectors,
                        visible only when the user picks Add-vest. Mirrors
                        the CustomFabricQuestionnaire vest section so the
                        pre-render and post-render controls stay coherent. */}
                    {silhVest === 'with' && (
                      <div className="mt-2 space-y-2" data-testid="silhouette-vest-detail">
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Vest style</p>
                          <div className="grid grid-cols-2 gap-1.5">
                            {[
                              { value: 'single_breasted', label: 'Single-Breasted' },
                              { value: 'double_breasted', label: 'Double-Breasted' },
                            ].map(opt => {
                              const active = silhVestStyle === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => {
                                    if (isRegenerating) return;
                                    setSilhVestStyle(opt.value);
                                    // Snap button + lapel to a coherent
                                    // default when the tailor switches
                                    // between SB and DB.
                                    if (opt.value === 'double_breasted') {
                                      setSilhVestButtons('6x2');
                                      setSilhVestLapel('peak');
                                    } else {
                                      setSilhVestButtons('5');
                                      setSilhVestLapel('none');
                                    }
                                  }}
                                  disabled={isRegenerating}
                                  className={`px-2 py-1.5 border text-[10px] uppercase tracking-wider transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                                  data-testid={`silhouette-vest-style-${opt.value}`}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Buttons</p>
                          <div className="grid grid-cols-3 gap-1.5">
                            {(silhVestStyle === 'double_breasted'
                              ? [{ value: '4x2', label: '4×2' }, { value: '6x2', label: '6×2' }]
                              : [{ value: '4', label: '4' }, { value: '5', label: '5' }, { value: '6', label: '6' }]
                            ).map(opt => {
                              const active = silhVestButtons === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => {
                                    if (isRegenerating) return;
                                    setSilhVestButtons(opt.value);
                                  }}
                                  disabled={isRegenerating}
                                  className={`px-2 py-1.5 border text-[10px] uppercase tracking-wider transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                                  data-testid={`silhouette-vest-buttons-${opt.value}`}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Vest lapel</p>
                          <div className="grid grid-cols-3 gap-1.5">
                            {(silhVestStyle === 'double_breasted'
                              ? [{ value: 'none', label: 'No lapel' }, { value: 'peak', label: 'Peak' }, { value: 'shawl', label: 'Shawl' }, { value: 'notch', label: 'Notch' }]
                              : [{ value: 'none', label: 'No lapel' }, { value: 'notch', label: 'Notch' }, { value: 'shawl', label: 'Shawl' }]
                            ).map(opt => {
                              const active = silhVestLapel === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => {
                                    if (isRegenerating) return;
                                    setSilhVestLapel(opt.value);
                                  }}
                                  disabled={isRegenerating}
                                  className={`px-2 py-1.5 border text-[10px] uppercase tracking-wider transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                                  data-testid={`silhouette-vest-lapel-${opt.value}`}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Satin tuxedo lapel conversion (Iter 147) — redraws
                      the lapels as silk-satin-faced shawl or peak while
                      the silhouette lock keeps everything else parked.
                      Feb 2026 — TUXEDO-ONLY per user spec. Hidden for
                      all non-tuxedo silhouettes (business suits, sports
                      coats, blazers, etc.) so the section stays
                      contextually correct. */}
                  {isTuxedoLook && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Lapels · satin tuxedo</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { value: 'satin_shawl', label: 'Satin shawl' },
                        { value: 'satin_peak',  label: 'Satin peak' },
                      ].map(opt => {
                        const active = silhLapel === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              if (isRegenerating) return;
                              setSilhLapel(active ? null : opt.value);
                            }}
                            disabled={isRegenerating}
                            className={`px-2 py-1.5 border text-[10px] uppercase tracking-wider transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                            data-testid={`silhouette-lapel-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  )}
                  {/* Feb 2026 — TUXEDO-ONLY Bowtie ↔ Tie converter.
                      Replaces the general Tie Recolour section for
                      tuxedo cards (that row is hidden by
                      `_showTieRecolour` above when isTuxedoLook). Fires
                      an edit with `tie_type` set to bow_tie or
                      silk_solid so Gemini swaps the neckwear while
                      keeping fabric / face / pose locked. */}
                  {isTuxedoLook && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Neckwear · bowtie ↔ tie</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { value: 'bow_tie',    label: 'Bow tie' },
                        { value: 'silk_solid', label: 'Necktie' },
                      ].map(opt => {
                        const active = silhTieForm === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              if (isRegenerating) return;
                              setSilhTieForm(active ? null : opt.value);
                            }}
                            disabled={isRegenerating}
                            className={`px-2 py-1.5 border text-[10px] uppercase tracking-wider transition-all ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                            data-testid={`silhouette-tieform-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  )}
                  {/* Feb 2026 — TUXEDO-ONLY Trouser detail. One-click
                      pill that repaints the tuxedo trouser with the
                      classic black-tie side stripe (satin galon) down
                      each outside seam. Fires an isolated
                      trouser-only edit (routes through the backend's
                      structural_only_lock) so fabric, face, pose,
                      lapels, shirt, tie and shoes stay pixel-identical.
                      Sits outside the Apply-batch button because it's
                      a dedicated one-tap reinforcement, not a
                      multi-choice restructure. */}
                  {isTuxedoLook && (
                    <div data-testid="silhouette-tux-trouser-row">
                      <p className="text-[10px] uppercase tracking-widest text-[#52525B] mb-1.5">Trousers · satin side stripe</p>
                      <button
                        type="button"
                        onClick={() => {
                          if (isRegenerating) return;
                          setPendingTuxTrouser(TUXEDO_TROUSER_DETAIL.value);
                          instantRegenerate({
                            customTrouserStyle: TUXEDO_TROUSER_DETAIL.value,
                            __trouserCutOnly: true,
                          });
                        }}
                        disabled={isRegenerating}
                        className={`w-full px-3 py-2 border text-left transition-all ${pendingTuxTrouser === TUXEDO_TROUSER_DETAIL.value ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'} ${isRegenerating ? 'opacity-50 cursor-wait' : ''}`}
                        data-testid="silhouette-tux-trouser-satin-stripe"
                      >
                        <span className="block text-[10px] uppercase tracking-wider">{TUXEDO_TROUSER_DETAIL.label}</span>
                        <span className="block text-[9px] text-[#52525B] mt-0.5 normal-case">{TUXEDO_TROUSER_DETAIL.hint} · one-click apply</span>
                      </button>
                      {pendingTuxTrouser && (
                        <p className="text-[10px] text-[#52525B] mt-1.5" data-testid="silhouette-tux-trouser-active-label">
                          Applying satin side stripe — jacket, shirt, shoes and face locked.
                        </p>
                      )}
                    </div>
                  )}
                  {/* Apply — greyed out until at least one choice is
                      selected. Fires one surgical edit stacking every
                      selected field. */}
                  <button
                    type="button"
                    disabled={isRegenerating || (!silhJacketStyle && !silhJacketState && !silhVest && !silhLapel && !silhTieForm)}
                    onClick={() => {
                      instantRegenerate({
                        jacketStyleSilh: silhJacketStyle || undefined,
                        jacketStateSilh: silhJacketState || undefined,
                        vestSilh:        silhVest        || undefined,
                        lapelSilh:       silhLapel       || undefined,
                        tieFormSilh:     silhTieForm     || undefined,
                        // Feb 2026 — vest detail only crosses when the
                        // user selected Add vest. Backend ignores these
                        // fields when `vest === 'without'` or absent.
                        vestStyleSilh:   silhVest === 'with' ? (silhVestStyle   || undefined) : undefined,
                        vestButtonsSilh: silhVest === 'with' ? (silhVestButtons || undefined) : undefined,
                        vestLapelSilh:   silhVest === 'with' ? (silhVestLapel   || undefined) : undefined,
                        __silhouetteOnly: true,
                      });
                    }}
                    className="w-full px-3 py-2 border-2 text-[10px] uppercase tracking-widest transition-all border-[hsl(var(--accent))] bg-[hsl(var(--accent))] text-[#0A0A0C] font-semibold hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-transparent disabled:text-[#52525B] disabled:border-[#27272A]"
                    data-testid="silhouette-apply"
                  >
                    {isRegenerating ? 'Re-rendering…' : 'Apply silhouette edit'}
                  </button>
                  <p className="text-[10px] text-[#52525B]">
                    Jacket, shirt, trousers, shoes, fabric, and face all stay locked. Only the selected silhouette change is applied.
                  </p>
                </div>
              )}
            </div>
          )}
          </div>

          <div className="space-y-4">
            {/* Bespoke tailoring "Features" — accordion items that branch
                by garment type (suits / sports coats / safari jackets /
                shirts / outerwear). featuresFor() picks the right 5 to
                surface so the consultant walks the client through what's
                actually visible on THIS render. */}
            {(() => {
              const merged = featuresFor(look);
              if (!merged || merged.length === 0) return null;
              return (
                <div className="bg-[#0A0A0C] border border-[#27272A] p-4" data-testid="workspace-upgrades-list">
                  <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: '#EAB308' }}>Features</p>
                  <ul className="space-y-0">
                    {merged.map((item) => (
                      <UpgradeItem key={item.key} title={item.title} benefit={item.benefit} />
                    ))}
                  </ul>
                </div>
              );
            })()}
            {(() => {
              // 1. Prefer the structured Gemini-emitted package_chips when present (iter_49+ looks).
              // 2. Otherwise PARSE the legacy text bullets into chips on the client (every older look).
              // 3. As a last resort, fall through to the bullet list (chip parser couldn't extract anything).
              const structured = Array.isArray(look.tailor_notes?.package_chips) ? look.tailor_notes.package_chips : [];
              const fallbackText = Array.isArray(look.tailor_notes?.package) ? look.tailor_notes.package : [];
              const rawDerived = structured.length > 0 ? structured : parsePackageTextToChips(fallbackText);

              // ── Tab-aware filter (Feb 2026) ─────────────────────────────
              // Strip chips that don't make sense for the current look's
              // category so the panel matches each tab:
              //   • business_casual:  no neckwear (ties / bow ties) — these
              //                       are SEPARATES outfits with open collar.
              //   • outerwear:        no pocket squares — they live on the
              //                       BASE jacket, not the layering coat.
              //   • shirts:           no ties / pocket squares — standalone
              //                       shirt portraits, open collar.
              const cat = (look.category || '').toLowerCase();
              const isBusinessCasual = cat.startsWith('business_casual') || cat.includes('inspiration_business_c');
              const isOuterwear     = cat.startsWith('outerwear') || cat.includes('inspiration_outerwear');
              const isShirts        = cat.startsWith('shirts');
              const filtered = (rawDerived || []).filter(chip => {
                const k = (chip.kind || 'other').toLowerCase();
                const labelLower = String(chip.label || '').toLowerCase();
                const isTie       = (k === 'tie' || k === 'bow_tie');
                const isPocketSq  = (k === 'pocket_square') ||
                                    (k === 'accessory' && /\bpocket\s+square\b/.test(labelLower));
                if (isBusinessCasual && isTie) return false;
                if (isOuterwear && isPocketSq) return false;
                if (isShirts && (isTie || isPocketSq)) return false;
                return true;
              });

              // ── Belt = Shoes colour coherence (Feb 2026) ───────────────
              // Classic menswear rule: the belt MUST match the shoes
              // (same hide / same colour family). Gemini sometimes drifts
              // — e.g. dark-brown shoes paired with a black belt. When BOTH
              // chips exist, we pin the belt's effective swatch to the
              // shoes' swatch downstream. Pass the shoe hex as a hint.
              const shoeChip = filtered.find(c => (c.kind || '').toLowerCase() === 'shoes');
              const shoeHex = shoeChip ? (_hexForColorName(shoeChip.label) || shoeChip.color_hex) : null;
              const derived = shoeHex
                ? filtered.map(c => {
                    if ((c.kind || '').toLowerCase() !== 'belt') return c;
                    // Override the belt's effective colour to match the shoes.
                    return { ...c, _belt_match_hex: shoeHex };
                  })
                : filtered;

              if (derived.length === 0 && fallbackText.length === 0) return null;
              return (
                <div className="bg-[#0A0A0C] border border-[#27272A] p-4" data-testid="workspace-upsell-package">
                  <p className="text-[10px] uppercase tracking-wider mb-3" style={{ color: '#EAB308' }}>This looks good with</p>
                  {derived.length > 0 ? (
                    <PackageChips chips={derived} />
                  ) : (
                    <ul className="space-y-1.5">
                      {fallbackText.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2.5">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#EAB308' }} />
                          <span className="text-xs text-[#E4E4E7] leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })()}

            {/* Quick Tweaks removed per product decision (Feb 2026).
                The in-modal one-click style adjustment surface is being
                retired in favour of the more deliberate Customize flow.
                Code retained in git history for restoration if needed. */}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
